// challenge-grading — pure comparison logic shared by the student Check flow
// (challenge-chrome.js) and, implicitly, the teacher's checklist prepopulation
// (which reuses `compareCheckpoint`'s notion of "found"). No DOM, no network —
// safe to unit-test directly under Node.
//
// Comparison rules (decisions.md):
//   - A ticked checkpoint variable absent from the actual values (the student's
//     code never defined it) is reported "not found" — distinct from a mismatch.
//   - A scalar NUMBER checkpoint with a tolerance > 0 passes when
//     |actual - expected| <= tolerance.
//   - Every other checkpoint (non-numeric scalar, or list/dict) is exact-match:
//     list comparison is order-sensitive, dict comparison is key-order-
//     insensitive — i.e. Python's own `==` semantics for JSON-safe values.

function deepEqual(a, b) {
  if (a === b) { return true; }
  if (typeof a !== typeof b) { return false; }
  if (a === null || b === null) { return a === b; }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) { return false; }
    for (var i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) { return false; }
    }
    return true;
  }
  if (typeof a === 'object') {
    var ak = Object.keys(a);
    var bk = Object.keys(b);
    if (ak.length !== bk.length) { return false; }
    for (var j = 0; j < ak.length; j++) {
      var k = ak[j];
      if (!Object.prototype.hasOwnProperty.call(b, k) || !deepEqual(a[k], b[k])) { return false; }
    }
    return true;
  }
  return false;
}

// code-challenge-expected-output: normalize a captured stdout string with the
// teacher-chosen toggle pipeline, deterministically. Applied SYMMETRICALLY to
// the golden and the student output before comparison. Pure — no DOM/network.
//
// Toggle defaults (mirrors the schema in lib/models/material.js):
//   trailingWhitespace  ON  (step 1) — line-endings -> \n, strip per-line
//                             trailing whitespace, strip trailing blank lines.
//   collapseWhitespace  OFF (step 2) — collapse runs of internal whitespace to
//                             one space (and trim each line).
//   unicodeNormalize    OFF (step 3a) — Unicode NFKC.
//   caseFold            OFF (step 3b) — case-insensitive (lowercase).
//   stripPunctuation    OFF (step 3c) — remove ASCII punctuation.
//   keepNumericPunctuation
//                       OFF (step 3c modifier) — predict-answer-feedback: when
//                             `stripPunctuation` is on, KEEP `-` and `.` in the
//                             stripped set's exceptions, so `3.14` never
//                             normalizes to `314` and `-5` never to `5`. It is
//                             OPT-IN and default OFF precisely so Code Challenge
//                             and debug output stays byte-identical on live
//                             content: every caller reads its flags off a STRICT
//                             Mongoose subdoc that cannot carry an undeclared
//                             key, so no stored document can reach it. Only the
//                             predict surface passes it, in code.
//   numericTolerance    OFF (step 4) — number-tolerant compare. SHIPPED AS A
//                             DOCUMENTED FOLLOW-STUB: the toggle is stored and
//                             plumbed, but the comparator does not yet apply a
//                             tolerance (no tolerance magnitude is specified by
//                             the card/decisions — implementing one would be a
//                             guess). The other five toggles fully work.
// An absent `normalize` object ⇒ defaults (trailingWhitespace ON, rest OFF),
// so `!== false` selects ON-by-default and truthy checks select OFF-by-default.
export function normalizeOutput(text, normalizeOpts) {
  var opts = normalizeOpts || {};
  var out = (text == null) ? '' : String(text);

  // Step 1 (default ON): normalize line endings, strip per-line trailing
  // whitespace, strip trailing blank lines.
  if (opts.trailingWhitespace !== false) {
    out = out.replace(/\r\n?/g, '\n');
    out = out.split('\n').map(function (line) {
      return line.replace(/[ \t\f\v]+$/, '');
    }).join('\n');
    out = out.replace(/\n+$/, '');
  }

  // Step 2 (default OFF): collapse internal whitespace runs to a single space
  // and trim each line.
  if (opts.collapseWhitespace) {
    out = out.split('\n').map(function (line) {
      return line.replace(/[ \t\f\v]+/g, ' ').replace(/^[ \t\f\v]+|[ \t\f\v]+$/g, '');
    }).join('\n');
  }

  // Step 2b (default OFF, predict-only): remove EVERY space/tab within a line, so
  // `[10,20]` equals `[10, 20]`. Opt-in in code like `keepNumericPunctuation`: the
  // Mongoose subdocs Code Challenge and debug read their flags from cannot carry this
  // key, so their output stays byte-identical. Only the predict surface passes it.
  if (opts.ignoreAllSpaces) {
    out = out.replace(/[ \t\f\v]+/g, '');
  }

  // Step 3a (default OFF): Unicode NFKC.
  if (opts.unicodeNormalize && typeof out.normalize === 'function') {
    out = out.normalize('NFKC');
  }

  // Step 3b (default OFF): case-fold.
  if (opts.caseFold) {
    out = out.toLowerCase();
  }

  // Step 3c (default OFF): strip ASCII punctuation. `keepNumericPunctuation`
  // (default OFF) keeps the two characters that carry NUMERIC meaning — but only
  // WHERE THEY ACTUALLY CARRY IT, judged from the neighbouring characters:
  //
  //   `.` survives only BETWEEN two digits   — `3.14` stays `3.14`
  //   `-` survives only IMMEDIATELY BEFORE a digit and not after a word character
  //                                          — `-5` stays `-5`, `well-known` does not
  //
  // Everything else goes, INCLUDING a sentence-final full stop. An earlier version
  // exempted `.` and `-` wholesale by narrowing the character class, which kept far
  // more than "decimal points and minus signs": `Time to write some code.` kept its
  // full stop, so a student who forgave-punctuation still failed on it — and because
  // punctuation was then a FORGIVEN axis, excluded from the hint search, they were
  // told to check their SPELLING. Same for a stray `15.` against a golden of `15`.
  //
  // Single pass with a positional callback rather than sentinel substitution (a
  // sentinel can collide with real output) or lookbehind (unsupported in older
  // Safari, and an unsupported group is a PARSE error that kills the whole module).
  // `charAt` off either end returns '', which correctly fails both digit tests.
  if (opts.stripPunctuation) {
    var keepNumeric = !!opts.keepNumericPunctuation;
    out = out.replace(/[!-/:-@[-`{-~]/g, function (ch, at, whole) {
      if (!keepNumeric) { return ''; }
      var before = whole.charAt(at - 1), after = whole.charAt(at + 1);
      if (ch === '.' && /\d/.test(before) && /\d/.test(after)) { return ch; }
      if (ch === '-' && /\d/.test(after) && !/\w/.test(before)) { return ch; }
      return '';
    });
  }

  // Step 4 (default OFF): numeric-tolerance — documented follow-stub (see above).

  return out;
}

// code-challenge-expected-output: compare a golden stdout against the student's
// captured stdout, both normalized with the checkpoint's toggles. Returns a
// result compatible with the variable path's shape plus a friendly first-
// differing-line `diff` — NEVER the whole golden block (anti-gaming). Reported
// line numbers/quoted lines come from the NORMALIZED sequences so "line N"
// matches what was actually compared (decisions §J,§K,§L,§M,§N).
// friendlier-challenges: every result also carries `expected` / `got` — the NORMALISED
// strings the verdict compared (so a display highlight always matches pass/fail) — for the
// Expected / Your program printed columns (challenge-diff.js). DISPLAY ONLY: challenge-chrome's
// buildResultRow posts { kind, label, passed, diff } and never these two.
export function compareExpectedOutput(checkpoint, runStdout) {
  var label = checkpoint.label || 'Console output';
  var normOpts = checkpoint.normalize || {};
  var goldenNorm = normalizeOutput(checkpoint.goldenOutput, normOpts);
  var actualNorm = normalizeOutput(runStdout, normOpts);

  // Empty student output against a non-empty golden ⇒ a normal, friendly fail
  // (NOT a notFound crash).
  if (actualNorm.length === 0 && goldenNorm.length > 0) {
    return {
      kind: 'expected-output', label: label, passed: false,
      diff: "We didn't see any output from your program — did you use print()?",
      expected: goldenNorm, got: ''
    };
  }

  if (goldenNorm === actualNorm) {
    return { kind: 'expected-output', label: label, passed: true, diff: '', expected: goldenNorm, got: actualNorm };
  }

  var goldenLines = goldenNorm.split('\n');
  var actualLines = actualNorm.split('\n');
  var n = Math.min(goldenLines.length, actualLines.length);
  var diff = '';
  var found = false;
  for (var i = 0; i < n; i++) {
    if (goldenLines[i] !== actualLines[i]) {
      var spacingHint = '';
      // Lines that match once all whitespace is removed differ only by spacing.
      if (goldenLines[i].replace(/\s+/g, '') === actualLines[i].replace(/\s+/g, '')) {
        spacingHint = ' (check your spacing)';
      }
      // JSON.stringify each line so leading/trailing/internal spaces are visible
      // when rendered as plain textContent.
      diff = 'Your output differed on line ' + (i + 1) + ' — expected ' +
        JSON.stringify(goldenLines[i]) + ', got ' + JSON.stringify(actualLines[i]) + spacingHint;
      found = true;
      break;
    }
  }
  if (!found) {
    // Every shared line matched ⇒ the line counts differ.
    diff = 'expected ' + goldenLines.length + ' lines of output, got ' + actualLines.length;
  }

  return { kind: 'expected-output', label: label, passed: false, diff: diff,
    expected: goldenNorm, got: actualNorm };
}

// code-challenge-temporal-checkpoints (Stage 2): five deterministic frame-series
// evaluators for Pygame Zero challenges, graded over a per-variable value series
// `(number|null)[]` captured by the stepped runner engine. All PURE and unit-
// testable over a synthetic series. `direction` spelling is 'up'/'down' (R10);
// null holes follow the per-evaluator rules (R8). Each returns the shared result
// shape `{ kind, variable, label, passed, actual, diff }` — `diff` is an honest,
// frame-budget-naming message (never the whole series), `actual` the salient
// observed value (U13).
var TEMPORAL_KINDS = {
  reached: 1, settled: 1, 'stays-within': 1, monotonic: 1, 'crosses-threshold': 1,
  // code-challenge-flag-state-checkpoints (D2/G1): the two discrete-state kinds
  // JOIN the temporal family so `isTemporalKind` gates the stepped-run dispatch,
  // frame budget, and record-chrome row handling for them too. They are graded
  // from `discreteSeries` (not the numeric series) via explicit branches in
  // `compareCheckpoint` placed ABOVE the generic temporal branch.
  'flag-becomes': 1, 'state-sequence': 1,
  // code-challenge-collision-oscillation-liveness (D1/D6/D7): three more kinds
  // graded PURELY over the existing per-frame numeric `series` — no runner change,
  // no contract bump. `relational-series` (collision/gap/ratio between two actors)
  // reads MULTIPLE series (its var names live inside `expr`, so it has no single
  // `variable`); `oscillation` + `changed-from-initial` observe one `variable`.
  // All three JOIN the temporal family so `isTemporalKind` drives the stepped-run
  // dispatch + frame budget; each is routed by an explicit `compareCheckpoint`
  // branch ABOVE the generic temporal branch (relational because it needs the
  // whole series map, the other two so they never fall through the wrong path).
  'relational-series': 1, oscillation: 1, 'changed-from-initial': 1,
  // code-challenge-causal-event-checkpoints (D2/D5): `causal-delta` (one recorded
  // keyboard input → an expected response) and `event-normalized` (a consistent
  // per-event rate across many occurrences) JOIN the temporal family too — both
  // are graded PURELY over the existing per-frame series (numeric or discrete),
  // with NO runner change/contract bump. Routed by explicit `compareCheckpoint`
  // branches ABOVE the generic temporal branch (see evalCausalDelta/
  // evalEventNormalized below).
  'causal-delta': 1, 'event-normalized': 1,
  // code-challenge-dotted-path-checkpoints: `path-temporal` grades a value reached
  // via a discovered dotted path (`player.x`, `enemies[0].x`, `state["score"]`).
  // It JOINS the temporal family so `isTemporalKind` gates the stepped-run
  // dispatch/frame-budget/record-row handling; it is routed by an explicit
  // `compareCheckpoint` branch ABOVE the generic temporal branch and wraps one of
  // the 7 predicate types via `pathCheck.type`.
  'path-temporal': 1,
  // game-checkpoint-false-negative: `steps-down` means "this number went DOWN at
  // least once during the run", independent of its absolute level. It exists
  // because `crosses-threshold … down` on a COUNTER only passes when the counter
  // returns to the side it started on — so a student who banked 3 points before
  // the first hit failed a correct `score -= 1` (the M4.42 false negative). It is
  // DOWN-ONLY and carries NO fields beyond `variable` (no target, no direction,
  // no minDelta), which is what makes it additive at every boundary — no new
  // Mongoose field, no new Joi key, no FORMAT_VERSION bump. Membership HERE is
  // MANDATORY, not cosmetic: `isTemporalKind` gates the stepped-run dispatch AND
  // the record chrome's `restoreOrClassify`, which would otherwise silently
  // rewrite a re-recorded row to a legacy `exact` checkpoint.
  'steps-down': 1
};
var DEFAULT_SETTLE_FRAMES = 5; // M (decisions "Apply may decide"): sane v1 default.

// code-challenge-causal-event-checkpoints (D2/D5): the runner's stepped driver
// computes `frame = round(atMs / (1000 * DT))` internally (DT = 1/60) when it
// frame-gates recorded-input injection, but never emits this mapping back to the
// editor. Since `recordedInputs` are immutable between record-time and every
// check-time replay, this ONE shared constant lets both the record chrome
// (auto-suggest, computed once at record time) and this grader (read-only,
// consuming the already-computed `atFrame`/`atFrames`) agree on the exact same
// frame index with zero runner change and no contract bump.
export var FRAME_DT = 1 / 60;

export function isTemporalKind(kind) {
  return !!(kind && Object.prototype.hasOwnProperty.call(TEMPORAL_KINDS, kind));
}

// code-challenge-flag-state-checkpoints: the temporal-family members graded over
// the discrete (bool/string) series rather than the numeric one. Drives the
// by-kind Save-path re-grade routing (R1/R2) and the record-chrome row build (G7).
export function isDiscreteKind(kind) {
  return kind === 'flag-becomes' || kind === 'state-sequence';
}

function numberOr(v, fallback) {
  return (typeof v === 'number' && !isNaN(v)) ? v : fallback;
}

// Compact numeric display for a friendly message — integers verbatim, floats to
// at most 2 decimals. Never affects the raw `actual` value passed through.
function formatNum(n) {
  if (n === null || n === undefined || (typeof n === 'number' && isNaN(n))) { return '(no value)'; }
  if (typeof n !== 'number') { return String(n); }
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

function isNum(v) { return typeof v === 'number' && !isNaN(v); }

// Non-null numeric samples, each carrying its original frame index (R8: nulls
// are holes — an undefined/None/non-numeric sample that frame).
function nonNullSamples(series) {
  var out = [];
  for (var i = 0; i < series.length; i++) {
    if (isNum(series[i])) { out.push({ i: i, v: series[i] }); }
  }
  return out;
}

function finalNonNull(series) {
  for (var i = series.length - 1; i >= 0; i--) {
    if (isNum(series[i])) { return series[i]; }
  }
  return null;
}

function tResult(cp, passed, actual, diff) {
  return {
    kind: cp.kind,
    variable: cp.variable,
    label: cp.label || cp.variable,
    passed: passed,
    actual: actual,
    diff: passed ? '' : diff
  };
}

function budget(n) { return 'We ran your game for ' + n + ' frames; '; }

// reached — bounded-eventually (F[0,cap]): PASS if the value reaches `target`
// given `direction` at some frame ≤ cap (R8: non-null samples only).
function evalReached(cp, series) {
  var target = numberOr(cp.target, 0);
  var down = cp.direction === 'down';
  var samples = nonNullSamples(series);
  var N = series.length;
  if (samples.length === 0) {
    return tResult(cp, false, undefined,
      budget(N) + '`' + cp.variable + '` never took a numeric value — did you create it?');
  }
  var extreme = samples[0].v;
  var passed = false;
  for (var k = 0; k < samples.length; k++) {
    var v = samples[k].v;
    extreme = down ? Math.min(extreme, v) : Math.max(extreme, v);
    if (down ? (v <= target) : (v >= target)) { passed = true; }
  }
  var sym = down ? '≤' : '≥';
  return tResult(cp, passed, extreme,
    budget(N) + '`' + cp.variable + '` reached ' + formatNum(extreme) +
    ', but the target is ' + sym + ' ' + formatNum(target) + '.');
}

// settled — stable (|Δ| ≤ tolerance for `settleFrames` consecutive non-null
// frames) AND within tolerance of `target` (R9: single tolerance for both). A
// one-frame touch of target FAILS settled but would PASS reached.
function evalSettled(cp, series) {
  var target = numberOr(cp.target, 0);
  var tol = numberOr(cp.tolerance, 0);
  var need = Math.max(1, Math.round(numberOr(cp.settleFrames, DEFAULT_SETTLE_FRAMES)));
  var N = series.length;
  var w = 0, prev = null, passed = false;
  for (var i = 0; i < series.length; i++) {
    var v = series[i];
    if (isNum(v) && Math.abs(v - target) <= tol) {
      // Within tolerance of target: extend the window unless it jumped too far
      // from the previous in-window frame (a delta > tol restarts the window).
      if (w > 0 && prev !== null && Math.abs(v - prev) > tol) { w = 1; } else { w = w + 1; }
      prev = v;
    } else {
      w = 0; prev = null; // a null hole OR a value out of tolerance breaks the run.
    }
    if (w >= need) { passed = true; break; }
  }
  var last = finalNonNull(series);
  return tResult(cp, passed, last,
    budget(N) + '`' + cp.variable + '` never settled within ' + formatNum(tol) + ' of ' +
    formatNum(target) + ' for ' + need + ' frames in a row (it was ' + formatNum(last) + ' at the end).');
}

// stays-within — safety invariant (G): every non-null sample in [lo, hi]; nulls
// are ignored (an undefined var is not "out of bounds").
function evalStaysWithin(cp, series) {
  var lo = numberOr(cp.lo, -Infinity);
  var hi = numberOr(cp.hi, Infinity);
  var N = series.length;
  for (var i = 0; i < series.length; i++) {
    var v = series[i];
    if (isNum(v) && (v < lo || v > hi)) {
      return tResult(cp, false, v,
        budget(N) + '`' + cp.variable + '` left the range [' + formatNum(lo) + '…' +
        formatNum(hi) + '] at frame ' + i + ' (it was ' + formatNum(v) + ').');
    }
  }
  return tResult(cp, true, finalNonNull(series), '');
}

// monotonic — the non-null series is non-decreasing (up) or non-increasing
// (down) across frames (R8: compare consecutive non-null samples in order).
function evalMonotonic(cp, series) {
  var down = cp.direction === 'down';
  var samples = nonNullSamples(series);
  var N = series.length;
  for (var k = 1; k < samples.length; k++) {
    var a = samples[k - 1].v, b = samples[k].v;
    if (down ? (b > a) : (b < a)) {
      return tResult(cp, false, b,
        budget(N) + '`' + cp.variable + '` moved the wrong way at frame ' + samples[k].i +
        ' (from ' + formatNum(a) + ' to ' + formatNum(b) + '; expected it ' +
        (down ? 'never to increase' : 'never to decrease') + ').');
    }
  }
  return tResult(cp, true, samples.length ? samples[samples.length - 1].v : null, '');
}

// crosses-threshold — the series moves from one side of `target` to the other in
// the recorded `direction` (R9/R12): a strict sign change of (value − target).
// A value landing exactly ON target keeps the running sign (a zero is not "the
// other side"), so a single exact touch that returns to the same side does NOT
// count — only a genuine pass-through does.
function evalCrossesThreshold(cp, series) {
  var target = numberOr(cp.target, 0);
  var down = cp.direction === 'down';
  var samples = nonNullSamples(series);
  var N = series.length;
  var prevSign = 0;
  for (var k = 0; k < samples.length; k++) {
    var d = samples[k].v - target;
    var sign = d > 0 ? 1 : (d < 0 ? -1 : 0);
    if (sign === 0) { continue; } // on target: not a side; keep the running sign.
    if (prevSign !== 0 && sign !== prevSign) {
      var crossedUp = prevSign < 0 && sign > 0;
      if (down ? !crossedUp : crossedUp) { return tResult(cp, true, samples[k].v, ''); }
    }
    prevSign = sign;
  }
  // game-checkpoint-false-negative: today's sentence is emitted BYTE FOR BYTE
  // unchanged, and a near-miss clause is APPENDED to it when — and only when —
  // the clause is provably true. Appending (rather than rewriting) is what keeps
  // every existing label reading as English: the clause names no label, no
  // variable, no direction and no target, so M2.22's full-sentence label and
  // M2.16's "(down the screen)" parenthetical are untouched, and no pinned spec
  // asserting `to.contain('never crossed …')` can break.
  return tResult(cp, false, finalNonNull(series),
    budget(N) + '`' + cp.variable + '` never crossed ' + formatNum(target) +
    ' going ' + (down ? 'down' : 'up') + '.' + nearMissClause(samples, target, down));
}

// The appended near-miss clause for a FAILED `crosses-threshold`. Returns '' —
// today's message exactly — unless BOTH gates hold:
//   (a) at least one step between consecutive non-null samples moved in the
//       requested direction (so a frozen / never-moved value is never described
//       as having "got close"); AND
//   (b) EVERY non-null sample lies STRICTLY on the ORIGIN side of the target
//       (strictly below for `up`, strictly above for `down`).
// Gate (b) is what makes the superlative TRUE of the whole run. It also excludes,
// for free, the exact-touch case (a sample equal to the target is not strictly
// origin-side — and the base sentence is already complete for a touch that never
// crossed) and the overshoot case (where "closest" would understate what the
// student achieved). The clause states an observed fact and stops: no direction
// word, no sufficiency claim, no target, no variable or label.
// A third, purely PRESENTATIONAL gate (c) follows the extreme's computation: the
// clause is withheld when the extreme and the target share a printed form.
function nearMissClause(samples, target, down) {
  if (samples.length < 2) { return ''; }
  var moved = false;
  for (var k = 1; k < samples.length; k++) {
    var a = samples[k - 1].v, b = samples[k].v;
    if (down ? (b < a) : (b > a)) { moved = true; break; }
  }
  if (!moved) { return ''; }
  var extreme = samples[0].v;
  for (var j = 0; j < samples.length; j++) {
    var v = samples[j].v;
    // Strictly origin-side: above the target for `down`, below it for `up`.
    if (down ? !(v > target) : !(v < target)) { return ''; }
    extreme = down ? Math.min(extreme, v) : Math.max(extreme, v);
  }
  // ...and withhold it once more if the number we would PRINT is the target's own
  // printed form. `formatNum` rounds to 2dp, so an extreme within 0.005 of the
  // target (reachable on the float-position rows M2.16 `alien.y` 350 up and
  // M2.22 `alien.x` 600 up, though not on the integer counters) would render AS
  // the target and produce a self-contradictory pair of sentences: "never crossed
  // 350 going up. The closest it got was 350." Withholding is the same policy the
  // gates above apply — today's sentence is always correct on its own.
  if (formatNum(extreme) === formatNum(target)) { return ''; }
  return ' The closest it got was ' + formatNum(extreme) + '.';
}

// steps-down — the number went DOWN at least once, at ANY level (game-checkpoint-
// false-negative). PASS if any consecutive pair of non-null samples decreases;
// null holes are skipped exactly as `evalMonotonic` does. It carries no target
// and no direction.
//
// It FAILS CLOSED on a short series, following `evalReached` and deliberately NOT
// `evalMonotonic` — whose k=1 loop returns a VACUOUS PASS on 0/1 non-null samples.
// Copying that shape would grade GREEN a program whose watched variable never
// becomes numeric (never created, shadowed, always None, or a bool — which the
// sampler drops to a null hole on purpose): a false PASS shipped by the fix for a
// false negative. It NEVER sets `skipped` or `hintOnly`; both silently remove the
// row from `overallPassed`'s gating set.
//
// Branches evaluate in this fixed order, first match wins — a one-sample series
// also satisfies "never changed", so the order is load-bearing.
function evalStepsDown(cp, series) {
  var samples = nonNullSamples(series);
  var N = series.length;

  // 1. Never a number at all — the `evalReached` sentence, `actual: undefined`.
  if (samples.length === 0) {
    return tResult(cp, false, undefined,
      budget(N) + '`' + cp.variable + '` never took a numeric value — did you create it?');
  }
  // 2. Exactly one reading — it DID take a value, so say which one.
  if (samples.length === 1) {
    return tResult(cp, false, samples[0].v,
      budget(N) + 'we only ever saw one value for `' + cp.variable + '` (' +
      formatNum(samples[0].v) + '), so it never went down.');
  }
  // 3. A decreasing step anywhere — PASS. `actual` is the value it fell TO on
  //    that first decrease.
  var everChanged = false;
  for (var k = 1; k < samples.length; k++) {
    var a = samples[k - 1].v, b = samples[k].v;
    if (b < a) { return tResult(cp, true, b, ''); }
    if (b !== a) { everChanged = true; }
  }
  var last = finalNonNull(series);
  // 4. It never moved at all.
  if (!everChanged) {
    return tResult(cp, false, last,
      budget(N) + '`' + cp.variable + '` never changed at all — it stayed at ' +
      formatNum(last) + ' the whole time.');
  }
  // 5. It moved, but only ever upward.
  return tResult(cp, false, last,
    budget(N) + '`' + cp.variable + '` only ever went up (it ended at ' +
    formatNum(last) + '), so it never went down.');
}

// Dispatch a temporal checkpoint over its variable's value series. A missing/
// empty series (variable never requested/observed) is a friendly fail, not a
// crash — R5's 60-frame floor means a requested variable's series is never empty.
function compareTemporal(checkpoint, series) {
  if (!Array.isArray(series) || series.length === 0) {
    return {
      kind: checkpoint.kind, variable: checkpoint.variable,
      label: checkpoint.label || checkpoint.variable, passed: false, actual: undefined,
      diff: "We couldn't watch `" + checkpoint.variable + '` while running your game — did you create it?'
    };
  }
  switch (checkpoint.kind) {
    case 'reached': return evalReached(checkpoint, series);
    case 'settled': return evalSettled(checkpoint, series);
    case 'stays-within': return evalStaysWithin(checkpoint, series);
    case 'monotonic': return evalMonotonic(checkpoint, series);
    case 'crosses-threshold': return evalCrossesThreshold(checkpoint, series);
    // game-checkpoint-false-negative: this case is MANDATORY beside the
    // TEMPORAL_KINDS entry. Without it the `default` below returns
    // `passed:false, diff:'Unknown checkpoint kind.'` — which reproduces the
    // exact bug this change fixes.
    case 'steps-down': return evalStepsDown(checkpoint, series);
    default: return { kind: checkpoint.kind, variable: checkpoint.variable,
      label: checkpoint.label || checkpoint.variable, passed: false, actual: undefined,
      diff: 'Unknown checkpoint kind.' };
  }
}

// ---------------------------------------------------------------------------
// code-challenge-flag-state-checkpoints (D4): two discrete-state temporal
// evaluators graded over the per-frame DISCRETE series (bool/string/null)
// captured additively by the stepped runner — NOT the numeric series. Both are
// PURE and unit-testable over a synthetic discrete array. Feedback is non-leaky
// (D6): it names the student's own observed behaviour, never the golden target
// or the full expected order.

// checker-diagnostics B3 — observed-value display for failure diffs. Python-repr-like:
// True/False, numbers as formatNum (ints verbatim, floats to at most 2 decimals, trailing
// zeros dropped), strings in single quotes with no escaping; every value truncated to 30
// characters plus "…". Only ever applied to the STUDENT'S observed values.
function displayValue(v) {
  var text;
  if (v === true) { text = 'True'; }
  else if (v === false) { text = 'False'; }
  else if (typeof v === 'number') { text = formatNum(v); }
  else if (typeof v === 'string') { text = "'" + v + "'"; }
  else if (v === null || v === undefined) { text = 'None'; }
  else { text = String(v); }
  return text.length > 30 ? text.slice(0, 30) + '…' : text;
}

// A state name inside a "went a → b" run: strings bare, anything else repr-like; 30 chars.
function displayState(v) {
  if (typeof v === 'string') { return v.length > 30 ? v.slice(0, 30) + '…' : v; }
  return displayValue(v);
}

// The first `max` items mapped through `fmt`, then "…" when more were cut.
function capList(items, max, fmt) {
  var out = items.slice(0, max).map(fmt);
  if (items.length > max) { out.push('…'); }
  return out;
}

// "a → b → c" for a compressed observed state sequence (max 8 states, then "…").
function stateRun(seen) {
  return capList(seen, 8, displayState).join(' → ');
}

// Distinct non-null values in order of first appearance.
function distinctObserved(series) {
  var out = [];
  for (var i = 0; i < series.length; i++) {
    var v = series[i];
    if (v === null || v === undefined) { continue; }
    if (out.indexOf(v) === -1) { out.push(v); }
  }
  return out;
}

// A pure copy of the recorder's friendly key names (challenge-record-chrome.js
// KEY_FRIENDLY_NAMES / friendlyKeyName) — never imported from a chrome module.
var KEY_FRIENDLY_NAMES = {
  ArrowRight: 'the Right arrow', ArrowLeft: 'the Left arrow',
  ArrowUp: 'the Up arrow', ArrowDown: 'the Down arrow',
  Space: 'the Space bar', Enter: 'Enter'
};
function friendlyKeyName(code) {
  return KEY_FRIENDLY_NAMES[code] || ('the "' + code + '" key');
}

// Compress consecutive duplicates in a discrete series, dropping null holes, so
// `['menu','menu',null,'play','play','win'] → ['menu','play','win']`.
function compressStates(series) {
  var out = [];
  for (var i = 0; i < series.length; i++) {
    var v = series[i];
    if (v === null || v === undefined) { continue; }
    if (out.length === 0 || out[out.length - 1] !== v) { out.push(v); }
  }
  return out;
}

// `order` is an ordered subsequence of `seen` (greedy left-to-right match).
function isOrderedSubsequence(order, seen) {
  var j = 0;
  for (var i = 0; i < seen.length && j < order.length; i++) {
    if (seen[i] === order[j]) { j++; }
  }
  return j === order.length;
}

// flag-becomes — PASS if ANY frame's discrete value === expectedValue (D4). The
// fail message references the label/variable, never the target value (D6/G4).
function evalFlagBecomes(cp, series) {
  var arr = Array.isArray(series) ? series : null;
  var passed = false;
  if (arr) {
    for (var i = 0; i < arr.length; i++) {
      if (arr[i] === cp.expectedValue) { passed = true; break; }
    }
  }
  var diff;
  if (cp.label) {
    diff = "We didn't see this happen: " + cp.label + '.';
  } else {
    // checker-diagnostics B3: name what the student's game DID (observed values only —
    // never the target). Null samples are dropped; all-null keeps today's sentence.
    var observed = distinctObserved(arr || []);
    if (observed.length === 1) {
      diff = '`' + cp.variable + '` stayed ' + displayValue(observed[0]) + ' the whole run.';
    } else if (observed.length > 1) {
      diff = '`' + cp.variable + '` was ' + capList(observed, 5, displayValue).join(' then ') +
        ' — it never became the value your game needs.';
    } else {
      diff = '`' + cp.variable + '` never became the value your game needs during play.';
    }
  }
  return {
    kind: cp.kind, variable: cp.variable, label: cp.label || cp.variable,
    passed: passed, actual: undefined, diff: passed ? '' : diff
  };
}

// state-sequence — compress the student's consecutive-duplicate discrete values
// → `seen`; allowExtra!==false ⇒ `order` must be an ordered subsequence of
// `seen`; allowExtra===false ⇒ compressed `seen` must equal `order` exactly.
// Exact string ===, NO normalization (G2). Fail reports only the first missing
// state, else a generic out-of-order message — never the full order (D6/G5).
function evalStateSequence(cp, series) {
  var order = Array.isArray(cp.order) ? cp.order : [];
  var seen = compressStates(Array.isArray(series) ? series : []);
  var strict = cp.allowExtra === false;

  var passed;
  if (strict) {
    passed = seen.length === order.length;
    if (passed) {
      for (var k = 0; k < order.length; k++) {
        if (seen[k] !== order[k]) { passed = false; break; }
      }
    }
  } else {
    passed = isOrderedSubsequence(order, seen);
  }

  var diff = '';
  if (!passed) {
    var firstMissing = null;
    for (var m = 0; m < order.length; m++) {
      if (seen.indexOf(order[m]) === -1) { firstMissing = order[m]; break; }
    }
    // checker-diagnostics B3: name the student's OWN observed (compressed) sequence; still
    // only the FIRST missing state, and never the expected order. No non-null sample at all
    // keeps today's sentences.
    if (seen.length === 0) {
      if (firstMissing !== null) {
        diff = "Your game never reached the '" + firstMissing + "' state.";
      } else {
        diff = 'Your game passed through the states, but not in the expected order.';
      }
    } else if (firstMissing !== null && seen.length === 1) {
      diff = 'Your game never changed state. We expected ' + displayValue(firstMissing) + ' to happen.';
    } else if (firstMissing !== null) {
      diff = 'Your game went ' + stateRun(seen) + '. We expected ' + displayValue(firstMissing) +
        ' to happen too.';
    } else {
      diff = 'Your game went ' + stateRun(seen) + ', but not in the expected order.';
    }
  }
  return {
    kind: cp.kind, variable: cp.variable, label: cp.label || cp.variable,
    passed: passed, actual: undefined, diff: diff
  };
}

// compareDiscrete — dispatch a discrete-state kind over the discrete series,
// with the SAME empty-series friendly-fail guard as compareTemporal (R3): an
// absent/empty series (an internal Save-path caller, or a var that never took a
// bool/string value) fails friendly rather than throwing.
function compareDiscrete(checkpoint, series) {
  if (!Array.isArray(series) || series.length === 0) {
    return {
      kind: checkpoint.kind, variable: checkpoint.variable,
      label: checkpoint.label || checkpoint.variable, passed: false, actual: undefined,
      diff: "We couldn't watch `" + checkpoint.variable + '` while running your game — did you create it?'
    };
  }
  if (checkpoint.kind === 'flag-becomes') { return evalFlagBecomes(checkpoint, series); }
  return evalStateSequence(checkpoint, series);
}

// ---------------------------------------------------------------------------
// code-challenge-dotted-path-checkpoints: grade a value reached via a discovered
// dotted path. This stays SIMPLE (decisions §Grading): it reads whatever series
// ended up in `passData` under the checkpoint's own `path` key (literal, or
// fallback-resolved by the orchestrator in challenge-chrome.js — indistinguishable
// here), then DELEGATES to the SAME flat-field evaluators the standalone temporal/
// discrete kinds already use (compareTemporal/compareDiscrete), so grading
// semantics for each `pathCheck.type` exactly match its standalone kind — never a
// parallel reimplementation. NO fallback-matching logic lives here.

// Feedback subject fallback (decisions §Grading 2.4): cp.label || cp.pathLabel ||
// cp.path — `pathLabel` is required, so this never degrades to a bare path or a
// generic "value" placeholder.
function pathSubject(cp) {
  return (cp && (cp.label || cp.pathLabel || cp.path)) || 'this value';
}

function fmtPathValue(v) {
  if (typeof v !== 'number') { return String(v); }
  return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100);
}

// A specific, plain-language PASS sentence naming the actual rule + threshold
// ("...crossed 400 during play, as expected"). Shown ONLY on a pass — safe to name
// the target the student already met. Returns null for kinds with no numeric
// threshold so they keep their existing pass message. A FAIL never uses this (the
// wrapped evaluator's non-leaky diff stands, so the target is never revealed).
function pathPassSentence(pc) {
  pc = pc || {};
  var t = pc.target;
  var down = pc.direction === 'down';
  switch (pc.type) {
    case 'crosses-threshold': return 'This value crossed ' + fmtPathValue(t) + ' during play, as expected.';
    case 'reached': return down
      ? 'This value came down to ' + fmtPathValue(t) + ', as expected.'
      : 'This value reached ' + fmtPathValue(t) + ', as expected.';
    case 'settled': return 'This value settled around ' + fmtPathValue(t) + ', as expected.';
    case 'stays-within': return 'This value stayed between ' + fmtPathValue(pc.lo) + ' and ' + fmtPathValue(pc.hi) + ', as expected.';
    case 'monotonic': return down ? 'This value only went down, as expected.' : 'This value only went up, as expected.';
    default: return null;
  }
}

function comparePathTemporal(checkpoint, passData) {
  passData = passData || {};
  var subject = pathSubject(checkpoint);

  // Orchestrator skip marker: the naming-variety fallback found no candidate
  // clearing the similarity threshold. A distinct, non-punitive, non-leaky
  // skipped result — excluded from overallPassed + rendered with a neutral
  // status (challenge-chrome.js), never a false green pass.
  if (passData.skippedPaths && checkpoint && passData.skippedPaths[checkpoint.path]) {
    return {
      kind: 'path-temporal', path: checkpoint.path, variable: checkpoint.path,
      label: subject, passed: true, skipped: true, actual: undefined,
      diff: "We couldn't automatically match this to your code — it's worth a manual look."
    };
  }

  var pc = (checkpoint && checkpoint.pathCheck) || {};
  var type = pc.type;
  // Build a flat checkpoint the existing evaluators understand: pathCheck's fields
  // are named identically to each wrapped kind's own flat fields. `variable` is set
  // to the friendly subject so a numeric evaluator's feedback names the label, not
  // a bare path.
  var inner = {};
  for (var k in pc) { if (Object.prototype.hasOwnProperty.call(pc, k)) { inner[k] = pc[k]; } }
  inner.kind = type;
  inner.variable = subject;
  inner.label = checkpoint.label || checkpoint.pathLabel;

  var result;
  if (type === 'flag-becomes' || type === 'state-sequence') {
    var dseries = (passData.discreteSeries && passData.discreteSeries[checkpoint.path]) || null;
    result = compareDiscrete(inner, dseries);
  } else {
    var nseries = (passData.valueSeries && passData.valueSeries[checkpoint.path]) || null;
    result = compareTemporal(inner, nseries);
  }
  // Restamp as a path-temporal row (the underlying evaluator stamped the wrapped
  // kind); keep the friendly subject for display/POST.
  result.kind = 'path-temporal';
  result.path = checkpoint.path;
  result.variable = checkpoint.path;
  result.label = subject;
  // On a PASS, name the actual rule + threshold ("...crossed 400, as expected")
  // instead of the generic "matched what we expected" fallback. Only on pass — a
  // FAIL keeps the wrapped evaluator's non-leaky diff (target never revealed).
  if (result.passed && !result.skipped) {
    var sentence = pathPassSentence(pc);
    if (sentence) { result.diff = sentence; }
  }
  return result;
}

// ---------------------------------------------------------------------------
// code-challenge-collision-oscillation-liveness (D1/D6/D7): three deterministic
// evaluators over the existing per-frame NUMERIC series (`passData.valueSeries`).
// All PURE and unit-testable over synthetic series. No runner change, no contract
// bump — the stepped driver already samples any requested top-level numeric var.
// Feedback is non-leaky (D6): it names the checkpoint's friendly `label` and the
// student's OWN observed value, never the target/reference.

function numericSeriesOf(valueSeries, name) {
  var s = valueSeries && name != null ? valueSeries[name] : null;
  return Array.isArray(s) ? s : null;
}

// Build the derived per-frame series for a `relational-series` `expr`. Same length
// as the shortest referenced series; a frame is a null HOLE (never contact) when
// any referenced component is null/missing. `distance` = 2-D Euclidean between two
// {x,y} points; `difference` = a − b; `ratio` = a / b (0 denominator → null hole).
function buildRelationalSeries(expr, valueSeries) {
  expr = expr || {};
  var type = expr.type;
  if (type === 'difference' || type === 'ratio') {
    var sa = numericSeriesOf(valueSeries, expr.a);
    var sb = numericSeriesOf(valueSeries, expr.b);
    if (!sa || !sb) { return []; }
    var n = Math.min(sa.length, sb.length);
    var out = [];
    for (var i = 0; i < n; i++) {
      if (isNum(sa[i]) && isNum(sb[i])) {
        out.push(type === 'ratio' ? (sb[i] === 0 ? null : sa[i] / sb[i]) : (sa[i] - sb[i]));
      } else { out.push(null); }
    }
    return out;
  }
  // default: `distance` between two {x,y} points (up to four top-level vars).
  var a = expr.a || {}, b = expr.b || {};
  var ax = numericSeriesOf(valueSeries, a.x), ay = numericSeriesOf(valueSeries, a.y);
  var bx = numericSeriesOf(valueSeries, b.x), by = numericSeriesOf(valueSeries, b.y);
  if (!ax || !ay || !bx || !by) { return []; }
  var m = Math.min(ax.length, ay.length, bx.length, by.length);
  var res = [];
  for (var k = 0; k < m; k++) {
    if (isNum(ax[k]) && isNum(ay[k]) && isNum(bx[k]) && isNum(by[k])) {
      var dx = ax[k] - bx[k], dy = ay[k] - by[k];
      res.push(Math.sqrt(dx * dx + dy * dy));
    } else { res.push(null); }
  }
  return res;
}

// Evaluate a `predicate {op, target, tol}` at one derived value. `tol` (optional)
// widens the predicate by its absolute magnitude on the relevant side.
function predicateHolds(op, value, target, tol) {
  var t = (typeof tol === 'number' && !isNaN(tol)) ? Math.abs(tol) : 0;
  switch (op) {
    case '<': return value < target + t;
    case '>=': return value >= target - t;
    case '>': return value > target - t;
    case '<=': default: return value <= target + t; // default collision op
  }
}

// The top-level numeric var names referenced inside a `relational-series` `expr`
// (`distance` → a.x,a.y,b.x,b.y; `difference`/`ratio` → a,b). Exported so BOTH
// chromes (student Check + re-record) can add them to the stepped-run observation
// list — a relational-series checkpoint has no single `variable`. Deduped, only
// non-empty strings.
export function relationalExprVariables(expr) {
  expr = expr || {};
  var names = [];
  if (expr.type === 'difference' || expr.type === 'ratio') {
    names = [expr.a, expr.b];
  } else {
    var a = expr.a || {}, b = expr.b || {};
    names = [a.x, a.y, b.x, b.y];
  }
  var seen = {}, out = [];
  for (var i = 0; i < names.length; i++) {
    var nm = names[i];
    if (typeof nm === 'string' && nm && !seen[nm]) { seen[nm] = 1; out.push(nm); }
  }
  return out;
}

function relationalLabel(cp) {
  return (cp.label && String(cp.label).trim()) ? cp.label : 'two actors';
}

// relational-series — a derived series satisfies a temporal predicate at ANY frame
// within the window (bounded-eventually, like `reached`). `budgetFrames` only
// narrows the window DOWN. 0 observable samples ⇒ skipped (never a vacuous pass).
function evalRelationalSeries(cp, valueSeries) {
  var expr = cp.expr || {};
  var pred = cp.predicate || {};
  var op = pred.op || '<=';
  var target = numberOr(pred.target, 0);
  var label = relationalLabel(cp);
  var derived = buildRelationalSeries(expr, valueSeries || {});
  var win = derived.length;
  if (typeof cp.budgetFrames === 'number' && cp.budgetFrames >= 0 && cp.budgetFrames < win) {
    win = cp.budgetFrames;
  }
  var samples = [];
  for (var i = 0; i < win; i++) { if (isNum(derived[i])) { samples.push(derived[i]); } }
  if (samples.length === 0) {
    return { kind: 'relational-series', label: label, passed: true, skipped: true,
      actual: undefined, diff: '' };
  }
  var wantsLower = (op === '<=' || op === '<');
  var closest = samples[0];
  var passed = false;
  for (var k = 0; k < samples.length; k++) {
    var v = samples[k];
    closest = wantsLower ? Math.min(closest, v) : Math.max(closest, v);
    if (predicateHolds(op, v, target, pred.tol)) { passed = true; }
  }
  var diff;
  if (expr.type === 'difference' || expr.type === 'ratio') {
    var noun = expr.type === 'difference' ? 'gap' : 'ratio';
    diff = 'The ' + noun + ' between the ' + label + ' never reached the target (closest it got was ' +
      formatNum(closest) + ').';
  } else {
    diff = 'The ' + label + ' never got close enough to collide (closest they came was ' +
      formatNum(closest) + ' pixels).';
  }
  return { kind: 'relational-series', label: label, passed: passed,
    actual: closest, diff: passed ? '' : diff };
}

function oscLabel(cp) {
  return (cp.label && String(cp.label).trim()) ? cp.label : (cp.variable || 'value');
}

// oscillation — PASS when the count of sign-changes of consecutive non-null first
// differences is ≥ `minReversals` AND the observed peak-to-trough amplitude lands
// within `amplitudeBand [lo, hi]` (lo<=hi enforced HERE — joi can't ref an index).
function evalOscillation(cp, series) {
  var arr = Array.isArray(series) ? series : [];
  var samples = nonNullSamples(arr);
  var label = oscLabel(cp);
  if (samples.length < 2) {
    return { kind: 'oscillation', variable: cp.variable, label: label,
      passed: true, skipped: true, actual: undefined, diff: '' };
  }
  var minReversals = Math.max(0, Math.round(numberOr(cp.minReversals, 0)));
  var band = Array.isArray(cp.amplitudeBand) ? cp.amplitudeBand : [];
  var lo = numberOr(band[0], 0);
  var hi = numberOr(band[1], Infinity);
  if (lo > hi) { var tmp = lo; lo = hi; hi = tmp; }
  var reversals = 0, prevSign = 0, minV = samples[0].v, maxV = samples[0].v;
  for (var k = 1; k < samples.length; k++) {
    var d = samples[k].v - samples[k - 1].v;
    if (samples[k].v < minV) { minV = samples[k].v; }
    if (samples[k].v > maxV) { maxV = samples[k].v; }
    var sign = d > 0 ? 1 : (d < 0 ? -1 : 0);
    if (sign !== 0) {
      if (prevSign !== 0 && sign !== prevSign) { reversals++; }
      prevSign = sign;
    }
  }
  var amplitude = maxV - minV;
  var enoughReversals = reversals >= minReversals;
  var inBand = amplitude >= lo && amplitude <= hi;
  var passed = enoughReversals && inBand;
  var diff = '';
  if (!passed) {
    if (!enoughReversals) {
      diff = 'Your ' + label + ' should keep moving back and forth, but it stopped moving.';
    } else if (amplitude < lo) {
      diff = 'Your ' + label + ' moves back and forth, but not far enough each time.';
    } else {
      diff = 'Your ' + label + ' swings too far — try keeping its movement smaller.';
    }
  }
  return { kind: 'oscillation', variable: cp.variable, label: label,
    passed: passed, actual: amplitude, diff: diff };
}

function livenessLabel(cp) {
  return (cp.label && String(cp.label).trim()) ? cp.label : (cp.variable || 'the game');
}

// changed-from-initial (liveness) — PASS if any non-null frame value differs from
// the first non-null (initial) value. HINT-ONLY by default (`hintOnly !== false`):
// the result carries `hintOnly:true` so overallPassed excludes it — a failing
// liveness row can never sink a student's Check unless the "require this" toggle
// cleared `hintOnly`.
function evalChangedFromInitial(cp, series) {
  var arr = Array.isArray(series) ? series : [];
  var samples = nonNullSamples(arr);
  var label = livenessLabel(cp);
  var isHint = cp.hintOnly !== false;
  var out;
  if (samples.length === 0) {
    out = { kind: 'changed-from-initial', variable: cp.variable, label: label,
      passed: true, skipped: true, actual: undefined, diff: '' };
  } else {
    var initial = samples[0].v;
    var changed = false;
    for (var k = 1; k < samples.length; k++) {
      if (samples[k].v !== initial) { changed = true; break; }
    }
    out = { kind: 'changed-from-initial', variable: cp.variable, label: label,
      passed: changed, actual: undefined,
      diff: changed ? '' : 'It looks like the game state never changed while it ran.' };
  }
  if (isHint) { out.hintOnly = true; }
  return out;
}

// ---------------------------------------------------------------------------
// code-challenge-causal-event-checkpoints (D2/D5): `causal-delta` grades a
// state change caused by ONE specific recorded keyboard input; `event-normalized`
// grades a consistent per-event rate across MULTIPLE occurrences of an event (a
// recorded keyboard input, or a rising edge of a `relational-series` predicate).
// Both are computed PURELY from the existing per-frame series returned by the
// stepped runner — `atFrame`/`atFrames` (for `event.type: 'input'`) are
// precomputed ONCE at record time from the immutable `recordedInputs`, so
// neither evaluator here needs raw recordedInputs. Non-leaky (D6): feedback
// names the input/event in friendly terms, never the raw minDelta/toValue/
// perEventDelta/tol/frame numbers.

function causalDeltaLabel(cp) {
  return (cp.label && String(cp.label).trim()) ? cp.label : 'the recorded key press';
}

// Nearest non-null sample at-or-before `idx` (falls forward to the first
// available sample if the run starts with holes). Mirrors the "closest known
// value" approach the rest of this module already uses.
function nearestNonNullBefore(series, idx) {
  var n = series.length;
  var start = Math.max(0, Math.min(n - 1, idx));
  for (var i = start; i >= 0; i--) {
    if (isNum(series[i])) { return series[i]; }
  }
  for (var j = start + 1; j < n; j++) {
    if (isNum(series[j])) { return series[j]; }
  }
  return null;
}

// causal-delta, discrete mode (D4) — mirrors `flag-becomes` semantics, windowed
// to just after the recorded input: PASS if the discrete value reaches
// `response.toValue` within `response.windowFrames` frames after `input.atFrame`.
function evalCausalDeltaDiscrete(cp, discreteSeries, atFrame, label) {
  var response = cp.response || {};
  var windowFrames = Math.max(0, Math.round(numberOr(response.windowFrames, 0)));
  var start = Math.max(0, atFrame);
  var end = Math.min(discreteSeries.length - 1, atFrame + windowFrames);
  var passed = false;
  for (var i = start; i <= end; i++) {
    if (discreteSeries[i] === response.toValue) { passed = true; break; }
  }
  var diff = 'We never saw the expected response after ' + label + '.';
  // checker-diagnostics B3: with a recorded key code, name the observed values (the value
  // just before the press, then the window after it) — never the expected value.
  var code = cp.input && cp.input.code;
  if (!passed && code) {
    var observed = [];
    for (var b = Math.min(discreteSeries.length - 1, atFrame - 1); b >= 0; b--) {
      if (discreteSeries[b] !== null && discreteSeries[b] !== undefined) {
        observed.push(discreteSeries[b]);
        break;
      }
    }
    for (var w = start; w <= end; w++) { observed.push(discreteSeries[w]); }
    var run = compressStates(observed);
    var subject = 'After you pressed ' + friendlyKeyName(code) + ', `' + cp.observe + '`';
    // Never name the expected value: a run that contains `toValue` (e.g. the value just
    // before the press already equalled it) keeps today's sentence.
    var targetText = displayValue(response.toValue);
    var namesTarget = run.some(function (v) {
      return v === response.toValue || displayValue(v) === targetText;
    });
    if (namesTarget) {
      // today's sentence (above)
    } else if (run.length === 1) {
      diff = subject + ' stayed ' + displayValue(run[0]) + '.';
    } else if (run.length > 1) {
      diff = subject + ' went ' + capList(run, 5, displayValue).join(' → ') +
        ' — not the change we expected.';
    }
  }
  return {
    kind: 'causal-delta', label: label, passed: passed, actual: undefined,
    diff: passed ? '' : diff
  };
}

// causal-delta, numeric mode — compares the value just before `atFrame` to its
// max/min over `response.windowFrames` frames after; PASS if the sign of the
// change matches `response.direction` AND its magnitude is at least
// `response.minDelta` (a lenient floor, not an exact match).
function evalCausalDeltaNumeric(cp, series, atFrame, label) {
  var response = cp.response || {};
  var windowFrames = Math.max(0, Math.round(numberOr(response.windowFrames, 0)));
  var before = nearestNonNullBefore(series, atFrame - 1);
  var start = Math.max(0, atFrame);
  var end = Math.min(series.length - 1, atFrame + windowFrames);
  var samples = [];
  for (var i = start; i <= end; i++) { if (isNum(series[i])) { samples.push(series[i]); } }
  var diff = 'Pressing ' + label + " didn't cause the change we expected.";
  if (before === null || samples.length === 0) {
    return { kind: 'causal-delta', label: label, passed: false, actual: undefined, diff: diff };
  }
  var direction = response.direction === 'decrease' ? 'decrease' : 'increase';
  var minDelta = Math.max(0, numberOr(response.minDelta, 0));
  var extremum = direction === 'increase' ? Math.max.apply(null, samples) : Math.min.apply(null, samples);
  var delta = extremum - before;
  var expectedSign = direction === 'increase' ? 1 : -1;
  var actualSign = delta > 0 ? 1 : (delta < 0 ? -1 : 0);
  var passed = actualSign === expectedSign && Math.abs(delta) >= minDelta;
  // checker-diagnostics B3: with a recorded key code, name the key, the observed before value
  // and the observed extremum the verdict compared, plus the expected DIRECTION — never
  // minDelta, the window or the frame.
  var code = cp.input && cp.input.code;
  if (!passed && code) {
    var subject = 'After you pressed ' + friendlyKeyName(code) + ', `' + cp.observe + '`';
    var dirWord = direction === 'increase' ? 'go up' : 'go down';
    // A real move whose two ends round to the same 2-dp text is printed unrounded, so the
    // sentence never reads "from 400 to 400".
    var beforeText = formatNum(before);
    var afterText = formatNum(extremum);
    if (actualSign !== 0 && beforeText === afterText) {
      beforeText = String(before);
      afterText = String(extremum);
    }
    if (actualSign === 0) {
      diff = subject + ' stayed at ' + formatNum(before) + '. We expected it to ' + dirWord + '.';
    } else if (actualSign !== expectedSign) {
      diff = subject + ' went from ' + beforeText + ' to ' + afterText +
        '. We expected it to ' + dirWord + '.';
    } else {
      diff = subject + ' only went from ' + beforeText + ' to ' + afterText +
        '. We expected a bigger change.';
    }
  }
  return { kind: 'causal-delta', label: label, passed: passed, actual: undefined, diff: passed ? '' : diff };
}

// causal-delta — dispatch discrete vs numeric mode by which series actually
// carries data for `observe` (checks `discreteSeries` first, same rule the
// auto-suggest heuristic uses). An `observe` present in neither series is a
// friendly fail, never a crash.
// The runner's stepped driver samples EVERY requested variable into BOTH
// `series` and `discreteSeries` every frame (one coerces to null, the other
// carries the real value, depending on the live type that frame) — so
// `discreteSeries[observe]` is an array of the SAME length for every variable,
// numeric ones included, just all-null. Presence alone can't disambiguate
// discrete vs numeric mode; only actual non-null data can.
function hasAnyNonNull(arr) {
  if (!Array.isArray(arr)) { return false; }
  for (var i = 0; i < arr.length; i++) {
    if (arr[i] !== null && arr[i] !== undefined) { return true; }
  }
  return false;
}

function evalCausalDelta(cp, passData) {
  var pd = passData || {};
  var observe = cp.observe;
  var input = cp.input || {};
  var atFrame = Math.max(0, Math.round(numberOr(input.atFrame, 0)));
  var label = causalDeltaLabel(cp);
  var discrete = pd.discreteSeries && pd.discreteSeries[observe];
  if (hasAnyNonNull(discrete)) {
    return evalCausalDeltaDiscrete(cp, discrete, atFrame, label);
  }
  var series = pd.valueSeries && pd.valueSeries[observe];
  if (!hasAnyNonNull(series)) {
    return {
      kind: 'causal-delta', label: label, passed: false, actual: undefined,
      diff: "We couldn't watch that value while running your game — did you create it?"
    };
  }
  return evalCausalDeltaNumeric(cp, series, atFrame, label);
}

function eventNormalizedLabel(cp) {
  return (cp.label && String(cp.label).trim()) ? cp.label : 'This event';
}

// A short lookahead window used to find the "after" sample for one event
// occurrence (distinct from the checkpoint-level `windowFrames`, which does not
// apply here — event-normalized grades an AVERAGE per-event delta, not a single
// bounded-eventually search).
export var EVENT_LOOKAHEAD_FRAMES = 5;

// Average delta in `series` observed in a short window right after each frame in
// `atFrames`. An occurrence whose before/after value can't be resolved (holes at
// the edge of the run) is skipped, not counted as a zero delta.
function averageEventDeltas(series, atFrames) {
  var deltas = [];
  (atFrames || []).forEach(function (atFrame) {
    var before = nearestNonNullBefore(series, atFrame - 1);
    var afterEnd = Math.min(series.length - 1, atFrame + EVENT_LOOKAHEAD_FRAMES);
    var after = null;
    for (var i = Math.max(0, atFrame); i <= afterEnd; i++) {
      if (isNum(series[i])) { after = series[i]; break; }
    }
    if (before === null || after === null) { return; }
    deltas.push(after - before);
  });
  return deltas;
}

function mean(nums) {
  if (!nums.length) { return 0; }
  var sum = 0;
  for (var i = 0; i < nums.length; i++) { sum += nums[i]; }
  return sum / nums.length;
}

// The frame indices of rising (false→true) edges of a `relational-series`-shaped
// `expr`/`predicate`, derived LIVE from the student's own run (collision timing
// is emergent per-student and can't be precomputed at record time, unlike an
// input-type event's `atFrames`). Reuses the exact same `buildRelationalSeries`/
// `predicateHolds` helpers a standalone `relational-series` checkpoint uses.
export function risingEdgeFrames(expr, predicate, valueSeries) {
  var derived = buildRelationalSeries(expr, valueSeries || {});
  var pred = predicate || {};
  var op = pred.op || '<=';
  var target = numberOr(pred.target, 0);
  var tol = pred.tol;
  var frames = [];
  var wasTrue = false;
  for (var i = 0; i < derived.length; i++) {
    var v = derived[i];
    var isTrueNow = isNum(v) && predicateHolds(op, v, target, tol);
    if (isTrueNow && !wasTrue) { frames.push(i); }
    wasTrue = isTrueNow;
  }
  return frames;
}

// event-normalized — grade the AVERAGE observed delta in `observe` across every
// event occurrence against `perEventDelta` within `tol`. Zero occurrences (no
// matching recorded input, or no rising edge observed) ⇒ `skipped` (excluded
// from `overallPassed`), same pattern as 0-case `function-io`. Numeric-`observe`
// only (D4: no discrete analog for a per-event rate).
function evalEventNormalized(cp, passData) {
  var pd = passData || {};
  var observe = cp.observe;
  var event = cp.event || {};
  var label = eventNormalizedLabel(cp);
  var series = (pd.valueSeries && pd.valueSeries[observe]) || null;
  if (!Array.isArray(series) || series.length === 0) {
    return {
      kind: 'event-normalized', label: label, passed: false, actual: undefined,
      diff: "We couldn't watch that value while running your game — did you create it?"
    };
  }
  var atFrames;
  if (event.type === 'relational') {
    atFrames = risingEdgeFrames(event.expr, event.predicate, pd.valueSeries || {});
  } else {
    atFrames = Array.isArray(event.atFrames) ? event.atFrames : [];
  }
  if (!atFrames.length) {
    return { kind: 'event-normalized', label: label, passed: true, skipped: true, actual: undefined, diff: '' };
  }
  var deltas = averageEventDeltas(series, atFrames);
  if (!deltas.length) {
    return { kind: 'event-normalized', label: label, passed: true, skipped: true, actual: undefined, diff: '' };
  }
  var avg = mean(deltas);
  var perEventDelta = numberOr(cp.perEventDelta, 0);
  var tol = Math.max(0, numberOr(cp.tol, 0));
  var passed = Math.abs(avg - perEventDelta) <= tol;
  var diff = label + " should change the value by about the same amount each time, but it didn't.";
  return { kind: 'event-normalized', label: label, passed: passed, actual: undefined, diff: passed ? '' : diff };
}

// code-challenge-behavioral-structural (Stage 3): grade a console (`python`-mode)
// challenge by what the program actually DID at run time, from the raw facts the
// instrumented trace pass returns (challengeTrace → `{ behavioral, structural }`).
// The runner returns RAW facts; ALL matching lives here (design D2), so the
// vocabulary is owned in one place and grading stays unit-testable (tests feed
// facts directly). `behavioral` checkpoints GATE (the real check); `structural`
// checkpoints are hint-only and NEVER gate (see overallPassed).

// Behavioural results may name the expected count / a short expected sequence
// (decisions "anti-gaming reveal rule": that copy is the card's own — unlike the
// expected-output golden, which is never revealed). A long/capped sequence is
// summarised rather than dumped.
export function isBehavioralKind(kind) { return kind === 'behavioral'; }
export function isStructuralKind(kind) { return kind === 'structural'; }

// Friendly, plain-language encouragement (satisfied) / neutral hint (not) for
// each structural pattern. Structural rows render the message for BOTH states —
// a satisfied one is encouragement, an unsatisfied one a neutral hint (never a
// red failure), because structural never gates.
var STRUCTURAL_MESSAGES = {
  'has-loop': { yes: 'Good, you used a loop!', no: 'Hint: this task is best solved with a loop (for or while).' },
  'print-in-loop': { yes: 'Nice — you printed inside a loop.', no: 'Hint: try putting your print() inside the loop.' },
  'has-conditional': { yes: 'Good, you used an if statement.', no: 'Hint: try using an if statement.' }
};
var STRUCTURAL_LABELS = {
  'has-loop': 'Uses a loop',
  'print-in-loop': 'Prints inside a loop',
  'has-conditional': 'Uses a conditional'
};

// Map a structural `pattern` enum onto the runner's raw boolean facts. The
// vocabulary is a small fixed enum shared by producer, matcher, and record UI.
export function matchStructural(pattern, facts) {
  facts = facts || {};
  switch (pattern) {
    case 'has-loop': return !!(facts.hasFor || facts.hasWhile);
    case 'print-in-loop': return !!facts.printInLoop;
    case 'has-conditional': return !!facts.hasIf;
    default: return false;
  }
}

// Compact display of a captured/expected sequence: list a short one verbatim;
// summarise a long/capped one (decisions: summarise rather than dump).
function formatSequence(seq) {
  if (!Array.isArray(seq)) { return '(none)'; }
  if (seq.length === 0) { return 'nothing'; }
  if (seq.length <= 12) { return seq.map(String).join(', '); }
  return seq.length + ' values starting ' + seq.slice(0, 3).map(String).join(', ') + '…';
}

function behavioralLabel(cp) {
  if (cp.label && String(cp.label).trim()) { return cp.label; }
  if (cp.behavioralType === 'iteration-count') { return 'Loop repeats'; }
  if (cp.behavioralType === 'value-sequence') { return (cp.variable || 'variable') + ' values'; }
  if (cp.behavioralType === 'print-sequence') { return 'Printed values'; }
  return 'Behaviour';
}

function sequenceDiff(subject, actual, expected) {
  var n = Math.min(actual.length, expected.length);
  for (var i = 0; i < n; i++) {
    if (!deepEqual(actual[i], expected[i])) {
      return subject + ' ' + formatSequence(actual) + ' — the task expects ' +
        formatSequence(expected) + ' (first difference at position ' + (i + 1) + ').';
    }
  }
  return subject + ' ' + formatSequence(actual) + ' — the task expects ' + formatSequence(expected) + '.';
}

function compareBehavioral(checkpoint, behavioral) {
  behavioral = behavioral || {};
  var label = behavioralLabel(checkpoint);
  var bt = checkpoint.behavioralType;

  if (bt === 'iteration-count') {
    var expected = numberOr(checkpoint.expectedValue, 0);
    var actual = numberOr(behavioral.loopIterations, 0);
    var passed = actual === expected;
    return {
      kind: 'behavioral', behavioralType: bt, label: label, passed: passed, actual: actual,
      diff: passed ? '' : ('Your loop ran ' + actual + ' time' + (actual === 1 ? '' : 's') +
        ', but the task expects ' + expected + '.')
    };
  }

  if (bt === 'value-sequence') {
    var expSeq = Array.isArray(checkpoint.expectedValue) ? checkpoint.expectedValue : [];
    var actSeq = (behavioral.valueSequences && behavioral.valueSequences[checkpoint.variable]) || [];
    var seqPassed = deepEqual(expSeq, actSeq);
    return {
      kind: 'behavioral', behavioralType: bt, variable: checkpoint.variable, label: label,
      passed: seqPassed, actual: actSeq,
      diff: seqPassed ? '' : sequenceDiff('`' + checkpoint.variable + '` took the values', actSeq, expSeq)
    };
  }

  if (bt === 'print-sequence') {
    var expP = Array.isArray(checkpoint.expectedValue) ? checkpoint.expectedValue : [];
    var actP = Array.isArray(behavioral.printSequence) ? behavioral.printSequence : [];
    var pPassed = deepEqual(expP, actP);
    return {
      kind: 'behavioral', behavioralType: bt, label: label, passed: pPassed, actual: actP,
      diff: pPassed ? '' : sequenceDiff('You printed', actP, expP)
    };
  }

  return { kind: 'behavioral', behavioralType: bt, label: label, passed: false, actual: undefined,
    diff: 'Unknown behavioural check.' };
}

function compareStructural(checkpoint, structural) {
  var satisfied = matchStructural(checkpoint.pattern, structural);
  var msg = STRUCTURAL_MESSAGES[checkpoint.pattern] || { yes: 'Looks good.', no: 'Hint.' };
  var label = (checkpoint.label && String(checkpoint.label).trim()) ||
    STRUCTURAL_LABELS[checkpoint.pattern] || 'Structure';
  // `hintOnly` marks this row as never-gating for the renderer + overallPassed.
  // advisory-checkpoints: it IS now posted — the challengeCheck Joi whitelists it
  // and the stored sub-schema declares it — so the teacher's review surface can
  // tell an advisory row from a failure. `passed` reflects satisfaction only for
  // display, not for the pass/fail gate.
  return {
    kind: 'structural', pattern: checkpoint.pattern, label: label, hintOnly: true,
    passed: satisfied, diff: satisfied ? msg.yes : msg.no
  };
}

// code-challenge-function-io (grading v3 [1/3]): grade a console (`python`-mode)
// challenge by calling a named top-level function in isolation, from the RAW
// per-call facts the shared instrumented function-call pass returns
// (challengeFncall → `{ results, functions }`). The runner returns raw facts;
// ALL matching lives here (mirrors the behavioural design D2), so grading stays
// pure/unit-testable. A `function-io` checkpoint GATES (contributes to
// overallPassed) UNLESS it has zero cases, in which case it is SKIPPED (marked
// `skipped:true`; overallPassed excludes it — never a vacuous pass).

// Tiny inline Levenshtein — kept inline (not fast-levenshtein) to keep the
// browser bundle lean (decisions "Apply may decide").
function levenshtein(a, b) {
  a = String(a); b = String(b);
  var m = a.length, n = b.length;
  if (m === 0) { return n; }
  if (n === 0) { return m; }
  var prev = [], cur = [], i, j;
  for (j = 0; j <= n; j++) { prev[j] = j; }
  for (i = 1; i <= m; i++) {
    cur[0] = i;
    for (j = 1; j <= n; j++) {
      var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    for (j = 0; j <= n; j++) { prev[j] = cur[j]; }
  }
  return prev[n];
}

// Closest defined-name suggestion for a missing/misnamed function. Suggest ONLY
// when the nearest name is close (distance ≤ 2 OR ≤ ~40% of the target length),
// else return '' (the caller falls back to a plain "couldn't find" message).
export function closestNameHint(target, names) {
  if (!target || !Array.isArray(names) || names.length === 0) { return ''; }
  var t = String(target).toLowerCase();
  var best = null, bestD = Infinity;   // nearest by edit distance
  var stem = null, stemD = Infinity;   // nearest name that shares the target's stem
  for (var i = 0; i < names.length; i++) {
    var nm = names[i];
    if (!nm || nm === target) { continue; }
    var d = levenshtein(target, nm);
    if (d < bestD) { bestD = d; best = nm; }
    // A rename that keeps the original stem (e.g. `double` → `doubleNumber`) is a
    // strong signal even when the edit distance exceeds the threshold below.
    var lo = String(nm).toLowerCase();
    if (t.length >= 3 && (lo.indexOf(t) !== -1 || t.indexOf(lo) !== -1) && d < stemD) {
      stemD = d; stem = nm;
    }
  }
  if (stem !== null) { return ' — you defined `' + stem + '`, did you mean that?'; }
  var thresh = Math.max(2, Math.round(String(target).length * 0.4));
  return (best !== null && bestD <= thresh) ? (' — you defined `' + best + '`, did you mean that?') : '';
}

// function-io-stdin: a case's typed answers, or null when the case carries none. Only a real
// array counts (the runner's own test is `isinstance(stdin, list)`), so a missing, null or
// malformed `stdin` is "absent" at every site: matching, the runner batch and display.
export function caseStdin(kase) {
  return (kase && Array.isArray(kase.stdin)) ? kase.stdin : null;
}

// function-io-stdin: the ONE projection of a case into a `challengeFncall.calls[]` entry, shared
// by the full Check, "Check this step" and the record chrome's reference run. `stdin` (a copy)
// is added only when the case carries an array, so a case without typed answers yields exactly
// `{ functionName, args }` as before (the runner then keeps its blank-line answer).
export function fnCallFor(functionName, kase) {
  var call = { functionName: functionName, args: (kase && kase.args) || [] };
  var stdin = caseStdin(kase);
  if (stdin) { call.stdin = stdin.slice(); }
  return call;
}

// function-io-stdin: does a runner result belong to this case? Name is checked by the caller;
// this is `args` (Python-`==` deep equality, as before) plus `stdin` by JSON equality, with an
// absent `stdin` on either side read as null (a result carries the key only when its call did).
export function fnResultMatchesCase(result, kase) {
  if (!result || !kase || !deepEqual(result.args, kase.args)) { return false; }
  var a = Array.isArray(result.stdin) ? result.stdin : null;
  var b = caseStdin(kase);
  try { return JSON.stringify(a) === JSON.stringify(b); } catch (e) { return false; }
}

// Format a call `fname(a, b)` for a friendly, non-leaky message. Args are shown
// as JSON so strings/lists are unambiguous.
// function-io-stdin: a third `stdin` argument appends the typed answers, e.g.
// `play_lock(0) typing 10, 60, 37`; without one the text is exactly as before.
function formatCall(fname, args, stdin) {
  var parts = (Array.isArray(args) ? args : []).map(function (a) {
    try { return JSON.stringify(a); } catch (e) { return String(a); }
  });
  var typed = typedAnswersText(stdin);
  return (fname || 'function') + '(' + parts.join(', ') + ')' + (typed === null ? '' : ' typing ' + typed);
}

// function-io-stdin: the typed answers as a student reads them, or null when the case types
// none (absent / not an array). Answers are shown as typed, joined by ", "; an empty answer
// (just Enter) reads "(blank)", and an empty list reads "nothing".
export function typedAnswersText(stdin) {
  if (!Array.isArray(stdin)) { return null; }
  if (!stdin.length) { return 'nothing'; }
  return stdin.map(function (s) {
    var t = String(s == null ? '' : s);
    return t === '' ? '(blank)' : t;
  }).join(', ');
}

// function-io-stdin: a `prints` value quoted in a failure line is clipped to this many
// characters. The function-io failure is ONE sentence (`gradeFunctionCase` returns a message
// string only; challenge-diff.js's line diff is wired to the expected-output row alone), so a
// long multi-line room transcript is clipped rather than diffed.
export var PRINTS_QUOTE_MAX = 240;

// A PASS line quotes each printed line on its own ("A", then "B"): one quoted string would
// show "\n" marks.
function quotePrintedLines(text) {
  var lines = normalizeOutput(text, {}).split('\n').filter(function (l) { return l !== ''; });
  var s = lines.map(formatValue).join(', then ');
  return s.length <= PRINTS_QUOTE_MAX ? s : s.slice(0, PRINTS_QUOTE_MAX - 1) + '…';
}

// A `prints` FAILURE names only the first line that differs, as plain text (no JSON quotes, no
// "\n" marks), clipped to a window around the first differing character: the whole transcript
// of a room is a wall nobody can read in the step box. The window keeps PRINTS_LINE_LEAD
// characters before the difference and PRINTS_LINE_MAX in all.
export var PRINTS_LINE_MAX = 90;
var PRINTS_LINE_LEAD = 30;

function lineWindow(line, pos) {
  if (line.length <= PRINTS_LINE_MAX) { return line; }
  var start = Math.max(0, pos - PRINTS_LINE_LEAD);
  var end = start + PRINTS_LINE_MAX;
  return (start > 0 ? '…' : '') + line.slice(start, end) + (end < line.length ? '…' : '');
}

function firstDifferingChar(a, b) {
  var n = Math.min(a.length, b.length);
  for (var i = 0; i < n; i++) { if (a.charAt(i) !== b.charAt(i)) { return i; } }
  return n;
}

// The difference as "<head>\nExpected: “…”\nPrinted: “…”": the head finishes the sentence that
// starts with the call ("printed the wrong text on line 2."), and the step box splits the
// message on "\n" so Expected and Printed each get a line (anywhere else the break is just
// white space). A head with nothing to quote ("printed nothing.") has no detail lines.
function printedDifference(expectedText, actualText) {
  var want = normalizeOutput(expectedText, {}).split('\n');
  var got = normalizeOutput(actualText, {}).split('\n');
  if (got.length === 1 && got[0] === '') { return { head: 'printed nothing.', detail: '' }; }
  for (var i = 0; i < Math.max(want.length, got.length); i++) {
    if (i >= got.length) {
      return { head: 'stopped printing before line ' + (i + 1) + '.', detail: '\nExpected: “' + lineWindow(want[i], 0) + '”' };
    }
    if (i >= want.length) {
      return { head: 'printed an extra line.', detail: '\nPrinted: “' + lineWindow(got[i], 0) + '”' };
    }
    if (want[i] !== got[i]) {
      var pos = firstDifferingChar(want[i], got[i]);
      return {
        head: 'printed the wrong text on line ' + (i + 1) + '.',
        detail: '\nExpected: “' + lineWindow(want[i], pos) + '”\nPrinted: “' + lineWindow(got[i], pos) + '”'
      };
    }
  }
  return { head: 'printed the wrong text.', detail: '' };
}

// Render a value the way PYTHON writes it, not the way JSON does.
//
// These strings go straight into student-facing failure lines on a `python`-mode
// challenge ("`double(5)` should be 10, but your function returned …"), and the runner
// hands Python's None/True/False across the bridge as JS null/true/false. Plain
// JSON.stringify therefore told a student their function "returned null" — in Year 10 that
// lands four activities after M6.14 teaches the word is `None` and just before M6.24 Q4
// grades it, so the checker was contradicting the course at its busiest error path.
//
// Scope note: this is a TOP-LEVEL render only. A None/True/False nested inside a returned
// list or dict still goes through JSON.stringify and will read as null/true/false. No
// seeded challenge returns one today; widen this to a recursive renderer if one ever does.
function formatValue(v) {
  if (v === null) { return 'None'; }
  if (v === true) { return 'True'; }
  if (v === false) { return 'False'; }
  try { return JSON.stringify(v); } catch (e) { return String(v); }
}

// Float compare with a defensively-clamped positive absTol so a compare against
// zero cannot spuriously fail (decisions "absTol guard"). Do NOT trust joi.
function floatClose(a, b, floatTol) {
  var absTol = (floatTol && typeof floatTol.absTol === 'number' && floatTol.absTol > 0)
    ? floatTol.absTol : 1e-12;
  var relTol = (floatTol && typeof floatTol.relTol === 'number' && floatTol.relTol >= 0)
    ? floatTol.relTol : 1e-9;
  return Math.abs(a - b) <= absTol + relTol * Math.max(Math.abs(a), Math.abs(b));
}

// find-the-bug-activity: the function-io RETURN comparator, exported so the find-the-bug
// surface compares a returned value against a typed expectation with EXACTLY the rule
// `gradeFunctionCase` uses below (float tolerance when the expected value is a float, Python-`==`
// deep equality otherwise) — no second comparison rule.
export function valuesMatch(actual, expected, expectedIsFloat, floatTol) {
  if (expectedIsFloat && typeof actual === 'number' && typeof expected === 'number') {
    return floatClose(actual, expected, floatTol);
  }
  return deepEqual(actual, expected);
}

// Grade ONE case against its per-call result. Returns
// `{ passed, message }` where `message` is the friendly, non-leaky failure line
// (empty when passed). `compareMode` ∈ 'return'|'prints'|'both'.
function gradeFunctionCase(checkpoint, kase, result, compareMode) {
  var call = formatCall(checkpoint.functionName, kase.args, caseStdin(kase));

  // A case that crashed the student's function (decisions edge case): report as
  // a crash naming the exception type + the real student line, and what it
  // should have returned.
  if (result.exception) {
    var exc = result.exception;
    var where = (exc.line != null) ? (' at line ' + exc.line) : '';
    var should = (compareMode === 'prints')
      ? ''
      : (' It should return ' + formatValue(kase.expected) + '.');
    return {
      passed: false,
      message: '`' + call + '` crashed with a ' + (exc.type || 'error') + where + '.' + should
    };
  }

  var returnPass = true, printsPass = true;
  if (compareMode === 'return' || compareMode === 'both') {
    if (!result.jsonSafe) {
      // Non-comparable student return (decisions edge case).
      return {
        passed: false,
        message: '`' + call + '` returned something I can’t compare — I expected ' +
          formatValue(kase.expected) + '.'
      };
    }
    if (kase.expectedIsFloat && typeof result.returnValue === 'number' &&
        typeof kase.expected === 'number') {
      returnPass = floatClose(result.returnValue, kase.expected, checkpoint.floatTol);
    } else {
      returnPass = deepEqual(result.returnValue, kase.expected);
    }
  }
  if (compareMode === 'prints' || compareMode === 'both') {
    printsPass = normalizeOutput(result.stdout, {}) === normalizeOutput(kase.expectedStdout, {});
  }

  if (returnPass && printsPass) { return { passed: true, message: '' }; }

  // Build the non-leaky failure line for the simplest-failing-case report.
  if (!returnPass && !printsPass && compareMode === 'both') {
    var bothDiff = printedDifference(kase.expectedStdout, result.stdout);
    return {
      passed: false,
      message: '`' + call + '` should return ' + formatValue(kase.expected) +
        ', but returned ' + formatValue(result.returnValue) + ', and it ' + bothDiff.head + bothDiff.detail
    };
  }
  if (!printsPass) {
    var diff = printedDifference(kase.expectedStdout, result.stdout);
    return { passed: false, message: '`' + call + '` ' + diff.head + diff.detail };
  }
  return {
    passed: false,
    message: '`' + call + '` should be ' + formatValue(kase.expected) +
      ', but your ' + (checkpoint.functionName || 'function') + ' function returned ' + formatValue(result.returnValue) + '.'
  };
}

// Compare a `function-io` checkpoint. `fncallData` = { results, functions } from
// the shared function-call pass. Returns `{ kind, label, passed, diff, actual }`
// (+ `skipped:true` for the 0-case case). Reports only the SIMPLEST failing
// case (smallest args JSON footprint; ties by authoring order) — never the
// reference source or the whole case table.
export function compareFunctionIO(checkpoint, fncallData) {
  fncallData = fncallData || {};
  var fname = checkpoint.functionName;
  var label = checkpoint.label || (fname ? (fname + '()') : 'Function');
  var cases = Array.isArray(checkpoint.cases) ? checkpoint.cases : [];
  var results = Array.isArray(fncallData.results) ? fncallData.results : [];
  var functions = Array.isArray(fncallData.functions) ? fncallData.functions : [];

  // 0-case ⇒ skipped, never gates (decisions "empty/vacuous guard").
  if (cases.length === 0) {
    return { kind: 'function-io', label: label, passed: true, skipped: true, diff: '', actual: undefined };
  }

  var ownResults = results.filter(function (r) { return r && r.functionName === fname; });
  var anyFound = ownResults.some(function (r) { return !r.notFound; });

  // Missing/misnamed function ⇒ one gating fail with a friendly closest-name hint
  // (derived from the student's real top-level defs).
  if (!anyFound) {
    var definedNames = functions.map(function (f) { return f && f.name; })
      .filter(function (nm) { return !!nm; });
    var hint = closestNameHint(fname, definedNames);
    // The hint already ends in its own '?' — appending a full stop unconditionally
    // produced "did you mean that?." on the single most-seen message in a functions
    // module. Punctuate the no-hint case only; `closestNameHint`'s own return value is
    // deliberately left untouched because it is exported and asserted on directly.
    return {
      kind: 'function-io', label: label, passed: false, actual: undefined,
      diff: hint
        ? ('I couldn’t find a function called `' + fname + '`' + hint)
        : ('I couldn’t find a function called `' + fname + '`.' +
          // No function found at all: a file may have failed to run, so point at the console.
          (definedNames.length === 0 ? ' If a file has an error in it, the console shows which one.' : ''))
    };
  }

  var compareMode = checkpoint.compareMode || 'return';
  var worst = null; // the simplest failing case's { message, args, got }
  // return-evidence (Trello DyXzvVbP): what the checker SAW for every case, in authoring order,
  // built only from the facts already in hand. DISPLAY-ONLY — it never feeds `passed`/`diff`,
  // and `buildResultRow` in challenge-chrome.js never posts it. Formatted by
  // function-evidence.js; values are pre-rendered here so the Python spelling (None/True/False)
  // has ONE source, `formatValue`.
  var evidence = [];

  for (var c = 0; c < cases.length; c++) {
    var kase = cases[c] || {};
    var result = null;
    for (var r = 0; r < ownResults.length; r++) {
      if (!ownResults[r].notFound && fnResultMatchesCase(ownResults[r], kase)) {
        result = ownResults[r]; break;
      }
    }
    if (result === null) {
      // Defensive: the batch should carry every case; a missing result is a fail.
      var missMsg = '`' + formatCall(fname, kase.args, caseStdin(kase)) + '` could not be run.';
      var missLen = jsonLen(kase.args);
      var missN = typedCount(kase);
      if (simpler(worst, missLen, missN)) { worst = { message: missMsg, len: missLen, n: missN, got: undefined }; }
      evidence.push(withTyped({ call: formatCall(fname, kase.args), mode: compareMode, passed: false, outcome: 'missing' }, kase));
      continue;
    }
    var graded = gradeFunctionCase(checkpoint, kase, result, compareMode);
    evidence.push(caseEvidence(fname, kase, result, compareMode, graded.passed));
    if (!graded.passed) {
      var len = jsonLen(kase.args);
      var n = typedCount(kase);
      // Simplest = smallest args JSON length, then fewest typed answers (function-io-stdin);
      // ties keep the earliest (strict <).
      if (simpler(worst, len, n)) {
        worst = { message: graded.message, len: len, n: n, got: result.returnValue };
      }
    }
  }

  if (worst === null) {
    return { kind: 'function-io', label: label, passed: true, diff: '', actual: undefined, evidence: evidence };
  }
  return { kind: 'function-io', label: label, passed: false, diff: worst.message, actual: worst.got,
    evidence: evidence };
}

// return-evidence (Trello DyXzvVbP): one case's display facts. `passed` is the verdict
// `gradeFunctionCase` already reached — this never re-grades.
function caseEvidence(fname, kase, result, compareMode, passed) {
  var ev = withTyped({ call: formatCall(fname, kase.args), mode: compareMode, passed: !!passed, outcome: 'ok' }, kase);
  var wantsReturn = compareMode === 'return' || compareMode === 'both';
  var wantsPrints = compareMode === 'prints' || compareMode === 'both';
  if (wantsReturn) { ev.expected = formatValue(kase.expected); }
  if (wantsPrints) { ev.expectedPrinted = formatValue(normalizeOutput(kase.expectedStdout, {})); }
  if (result.exception) {
    ev.outcome = 'crash';
    ev.errorType = String((result.exception && result.exception.type) || 'error');
    return ev;
  }
  if (wantsReturn && !result.jsonSafe) {
    ev.outcome = 'uncomparable';
    return ev;
  }
  if (wantsReturn) { ev.got = formatValue(result.returnValue); }
  if (wantsPrints) { ev.gotPrinted = formatValue(normalizeOutput(result.stdout, {})); }
  return ev;
}

// function-io-stdin: the evidence line carries the typed answers in their OWN `typed` field
// (function-evidence.js clips `call` and `typed` separately, so a long call never hides them);
// a case without stdin gets no key, so its evidence is unchanged.
function withTyped(ev, kase) {
  var typed = typedAnswersText(caseStdin(kase));
  if (typed !== null) { ev.typed = typed; }
  return ev;
}

function jsonLen(args) {
  try { return JSON.stringify(args == null ? [] : args).length; } catch (e) { return Infinity; }
}

// function-io-stdin: the second key of the "simplest failing case" choice — how many answers the
// case types (absent counts as 0, the same as `[]`).
function typedCount(kase) {
  var s = caseStdin(kase);
  return s ? s.length : 0;
}

// Is (len, n) strictly simpler than the current worst? Equal on both keeps the earlier case.
function simpler(worst, len, n) {
  if (worst === null) { return true; }
  return len < worst.len || (len === worst.len && n < worst.n);
}

// code-challenge-exception-checkpoints (grading v3 [2/3]): grade a console
// (`python`-mode) challenge by WHAT HAPPENED when the whole program ran under a
// canned stdin scenario, from the RAW facts the shared canned-stdin console-run
// pass returns (challengeConsoleRun → `{ stdout, exception, inputReads,
// outputAfterLastInput, ranOutOfInput }`). The runner returns raw facts; ALL
// matching lives here (mirrors the function-io / behavioural design), so grading
// stays pure/unit-testable. All three kinds GATE (they never set
// `kind:'structural'`/`skipped`, so overallPassed counts them unchanged).
//
// Each checkpoint is graded against the run made under ITS OWN `triggerStdin`:
// `consoleRunData.byStdin[JSON.stringify(cp.triggerStdin || [])]`. Feedback is
// friendly and non-leaky — the student's real crash line, never the reference
// source. `mro` (the raised class's base-chain) gives isinstance-style subclass
// matching in JS with no Python type registry (design D3).
var CONSOLE_RUN_KINDS = { 'runs-clean': 1, 'raises-on': 1, 'handles-gracefully': 1 };

export function isConsoleRunKind(kind) {
  return !!(kind && Object.prototype.hasOwnProperty.call(CONSOLE_RUN_KINDS, kind));
}

export function consoleRunStdinKey(triggerStdin) {
  return JSON.stringify(Array.isArray(triggerStdin) ? triggerStdin : []);
}

function consoleRunDefaultLabel(cp) {
  if (cp.kind === 'runs-clean') { return 'Runs without crashing'; }
  if (cp.kind === 'raises-on') { return 'Rejects invalid input'; }
  if (cp.kind === 'handles-gracefully') { return 'Handles invalid input'; }
  return 'Input handling';
}

// Friendly one-line rendering of a stdin scenario (a short array of typed lines).
function formatStdin(triggerStdin) {
  var arr = Array.isArray(triggerStdin) ? triggerStdin : [];
  if (arr.length === 0) { return ''; }
  return arr.map(function (s) { return JSON.stringify(String(s)); }).join(', ');
}

// A crash message at the student's REAL line (never the reference source). Uses
// the student's own exception text (their code), which already reads
// "ValueError: <message>".
function crashDiff(run) {
  var exc = run.exception || {};
  var where = (exc.line != null) ? (' on line ' + exc.line) : '';
  var what = (exc.text && String(exc.text).trim()) ? exc.text : (exc.type || 'an error');
  return 'Your program crashed with `' + what + '`' + where + '.';
}

function gradeRunsClean(cp, run, label) {
  if (run.ranOutOfInput) {
    return { kind: 'runs-clean', label: label, passed: false, actual: undefined,
      diff: 'Your program is waiting for input that isn’t coming — make sure it stops ' +
        'asking once it has the input it needs.' };
  }
  if (run.exception) {
    return { kind: 'runs-clean', label: label, passed: false, actual: (run.exception.type || null),
      diff: crashDiff(run) };
  }
  return { kind: 'runs-clean', label: label, passed: true, actual: null, diff: '' };
}

function gradeRaisesOn(cp, run, label) {
  var expected = cp.expectedExceptionType;
  var scenario = formatStdin(cp.triggerStdin);
  var phrase = scenario ? ('`' + scenario + '`') : 'that input';
  // A genuine (non-EOF) uncaught exception is required. ranOutOfInput is NOT a
  // match (the program looped waiting for input, it did not reject anything).
  if (run.exception && !run.ranOutOfInput) {
    var exc = run.exception;
    var mro = Array.isArray(exc.mro) ? exc.mro : [];
    var matched = exc.type === expected || mro.indexOf(expected) !== -1;
    if (matched) {
      return { kind: 'raises-on', label: label, passed: true, actual: exc.type,
        diff: 'Your program correctly rejects ' + phrase + ' with a `' + (exc.type || expected) + '`.' };
    }
    return { kind: 'raises-on', label: label, passed: false, actual: exc.type,
      diff: 'Expected a `' + expected + '` error when the user types ' + phrase +
        ', but got a `' + exc.type + '`.' };
  }
  return { kind: 'raises-on', label: label, passed: false, actual: null,
    diff: 'Expected your program to reject ' + phrase + ' with a `' + expected +
      '` error, but it didn’t.' };
}

function gradeHandlesGracefully(cp, run, label) {
  var scenario = formatStdin(cp.triggerStdin);
  var phrase = scenario ? ('`' + scenario + '`') : 'invalid input';
  // A genuine crash means the student did NOT recover.
  if (run.exception && !run.ranOutOfInput) {
    return { kind: 'handles-gracefully', label: label, passed: false, actual: (run.exception.type || null),
      diff: 'When the user types ' + phrase + ', your program should ask again instead of crashing.' };
  }
  // ranOutOfInput ⇒ the program never completed with the provided recovery input
  // (it looped past the good input we gave it).
  if (run.ranOutOfInput) {
    return { kind: 'handles-gracefully', label: label, passed: false, actual: undefined,
      diff: 'When the user types ' + phrase + ', your program should recover and carry on ' +
        'instead of waiting for more input.' };
  }
  // A graceful handler RE-PROMPTS: it reads PAST the bad input to reach a good one,
  // exactly as the reference did under this scenario. Requiring the re-read count is
  // the reliable signal — `outputAfterLastInput` alone is NOT, because a program that
  // merely echoes the bad input (`x = input(); print(x)`) also "prints after the read"
  // without recovering, which false-passed trivially-wrong code.
  var need = 1 + (typeof cp.minExtraInputReads === 'number' ? cp.minExtraInputReads : 1);
  if (run.inputReads >= need) {
    return { kind: 'handles-gracefully', label: label, passed: true, actual: run.inputReads, diff: '' };
  }
  return { kind: 'handles-gracefully', label: label, passed: false, actual: run.inputReads,
    diff: 'When the user types ' + phrase + ', your program should ask again for a valid value ' +
      'instead of moving on.' };
}

// Compare a console-run checkpoint (`runs-clean`/`raises-on`/`handles-gracefully`)
// against the run captured under its own `triggerStdin`. `consoleRunData` =
// `{ byStdin: { [stdinKey]: run } }`. Returns `{ kind, label, passed, diff, actual }`.
export function compareConsoleRun(checkpoint, consoleRunData) {
  consoleRunData = consoleRunData || {};
  var byStdin = consoleRunData.byStdin || {};
  var run = byStdin[consoleRunStdinKey(checkpoint.triggerStdin)];
  var label = checkpoint.label || consoleRunDefaultLabel(checkpoint);

  // Defensive: no run captured for this scenario ⇒ a friendly fail, not a crash.
  if (!run) {
    return { kind: checkpoint.kind, label: label, passed: false, actual: undefined,
      diff: "We couldn't test your program with that input — please try running the check again." };
  }
  if (checkpoint.kind === 'runs-clean') { return gradeRunsClean(checkpoint, run, label); }
  if (checkpoint.kind === 'raises-on') { return gradeRaisesOn(checkpoint, run, label); }
  if (checkpoint.kind === 'handles-gracefully') { return gradeHandlesGracefully(checkpoint, run, label); }
  return { kind: checkpoint.kind, label: label, passed: false, actual: undefined,
    diff: 'Unknown checkpoint kind.' };
}

// code-challenge-stdin-transcript (grading v3 [3/3]): grade a console
// (`python`-mode) challenge against a table of stdin cases (expected/boundary/
// invalid). Each case's run is the shared canned-stdin console-run pass keyed by
// `consoleRunData.byStdin[consoleRunStdinKey(case.stdin)]` — the run now carries
// the additive interleaved `transcript { turns:[{outputBefore,value}], tail }`.
// Two match modes: `prompt-independent` (default) strips the prompt LINE before
// each read and compares the remaining program output lines (numeric-aware token
// compare when `normalize.numericAware`), so a differently-worded prompt still
// passes; `full-transcript` diffs the whole normalized conversation. Feedback
// reports only the FIRST failing case (preferring boundary/invalid rows) and only
// the first differing line — never the reference source, the full golden
// transcript, or the whole case table. 0-row `caseTable` ⇒ `skipped` (mirrors
// function-io; excluded from overallPassed). This kind GATES (not structural, not
// skipped when it has cases).

// Rank for reporting the first failing case: boundary/invalid preferred over the
// demonstrated expected row.
function stdinCaseRank(tag) {
  if (tag === 'boundary') { return 0; }
  if (tag === 'invalid') { return 1; }
  return 2;
}

function orderedStdinCaseIndices(cases) {
  var idx = cases.map(function (_, i) { return i; });
  idx.sort(function (a, b) {
    var ra = stdinCaseRank(cases[a] && cases[a].tag);
    var rb = stdinCaseRank(cases[b] && cases[b].tag);
    if (ra !== rb) { return ra - rb; }
    return a - b; // stable within a tag group (authoring order)
  });
  return idx;
}

// Friendly rendering of a case's stdin as backticked values (spec example:
// "When the user enters `7`").
function formatCaseInput(stdin) {
  var arr = Array.isArray(stdin) ? stdin : [];
  if (arr.length === 0) { return 'that input'; }
  return arr.map(function (s) { return '`' + String(s) + '`'; }).join(', ');
}

// Does this line look like a prompt (the thing printed right before an input()
// read) rather than a genuine result line? Conservative: a line ending in a
// common prompt terminator, OR carrying no digits, is treated as a prompt; a line
// with digits and no terminator (a numeric result) is kept.
function looksLikePrompt(line) {
  var s = String(line == null ? '' : line).trim();
  if (s === '') { return false; }
  if (/[:>?»]\s*$/.test(line)) { return true; }
  if (!/\d/.test(s)) { return true; }
  return false;
}

// Strip the prompt line from one pre-input output segment: drop the LAST
// non-empty line iff it looks like a prompt; keep earlier real-output lines. The
// kept content ends with a newline so cross-turn concatenation stays line-aligned.
export function stripPromptLine(segment) {
  var normalized = String(segment == null ? '' : segment).replace(/\r\n?/g, '\n');
  var lines = normalized.split('\n');
  var last = -1;
  for (var i = lines.length - 1; i >= 0; i--) {
    if (lines[i].trim() !== '') { last = i; break; }
  }
  if (last === -1) { return ''; } // whitespace-only segment
  if (looksLikePrompt(lines[last])) { lines.splice(last, 1); }
  var kept = lines.join('\n').replace(/\n+$/, '');
  return kept === '' ? '' : (kept + '\n');
}

// The program's RESULT/value lines (prompt-independent): each turn's outputBefore
// minus its prompt line, plus the trailing output, normalized and split to lines.
function transcriptResultLines(transcript, normOpts) {
  var t = transcript || {};
  var turns = Array.isArray(t.turns) ? t.turns : [];
  var parts = [];
  for (var i = 0; i < turns.length; i++) {
    parts.push(stripPromptLine(turns[i] && turns[i].outputBefore));
  }
  parts.push(t.tail == null ? '' : String(t.tail));
  var norm = normalizeOutput(parts.join(''), normOpts);
  return norm === '' ? [] : norm.split('\n');
}

// step-checks fix round 1: the SAME prompt-stripped result lines as authored
// (original case, spacing and punctuation), for DISPLAY only. Only the checkpoint's
// own `trailingWhitespace` setting is applied (none of the other toggles changes the
// line count), so index i here is index i of `transcriptResultLines`.
function transcriptDisplayLines(transcript, normOpts) {
  return transcriptResultLines(transcript, { trailingWhitespace: (normOpts || {}).trailingWhitespace });
}

// How many leading lines every list shares, position by position.
function commonLeadingLines(lists) {
  if (lists.length === 0) { return 0; }
  var n = 0;
  for (;;) {
    var probe = lists[0][n];
    if (probe === undefined) { return n; }
    for (var k = 1; k < lists.length; k++) {
      if (lists[k][n] !== probe) { return n; }
    }
    n++;
  }
}

// Line i of `shown` (as authored), falling back to the normalised line.
function shownLine(shown, norm, i) {
  return i < shown.length ? shown[i] : norm[i];
}

// Reconstruct a terminal-like full transcript: each prompt + the typed value on
// its line, then the trailing output.
function renderFullTranscript(transcript) {
  var t = transcript || {};
  var turns = Array.isArray(t.turns) ? t.turns : [];
  var out = '';
  for (var i = 0; i < turns.length; i++) {
    var turn = turns[i] || {};
    out += (turn.outputBefore == null ? '' : String(turn.outputBefore)) +
      (turn.value == null ? '' : String(turn.value)) + '\n';
  }
  out += (t.tail == null ? '' : String(t.tail));
  return out;
}

// Strip surrounding (not internal) punctuation for the numeric-token test, then
// parse a finite number or return null.
function parseNumericToken(tok) {
  var s = String(tok == null ? '' : tok).replace(/^[^\w.+-]+/, '').replace(/[^\w.]+$/, '');
  if (s === '' || !/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(s)) { return null; }
  var n = Number(s);
  return isFinite(n) ? n : null;
}

// Compare two already-normalized result lines. Exact by default; when numericAware
// is on, whitespace-tokenise and compare pairwise — both-numeric tokens under
// floatClose, others exact; differing token counts fail.
function stdinLineMatches(exp, act, numericAware, floatTol) {
  if (exp === act) { return true; }
  if (!numericAware) { return false; }
  var et = exp.split(/\s+/).filter(function (x) { return x !== ''; });
  var at = act.split(/\s+/).filter(function (x) { return x !== ''; });
  if (et.length !== at.length) { return false; }
  for (var i = 0; i < et.length; i++) {
    var en = parseNumericToken(et[i]);
    var an = parseNumericToken(at[i]);
    if (en !== null && an !== null) {
      if (!floatClose(en, an, floatTol)) { return false; }
    } else if (et[i] !== at[i]) {
      return false;
    }
  }
  return true;
}

function gradePromptIndependentCase(expectedT, studentT, normOpts, numericAware, floatTol, phrase) {
  var expLines = transcriptResultLines(expectedT, normOpts);
  var actLines = transcriptResultLines(studentT, normOpts);
  var n = Math.min(expLines.length, actLines.length);
  for (var i = 0; i < n; i++) {
    if (!stdinLineMatches(expLines[i], actLines[i], numericAware, floatTol)) {
      return { passed: false, actual: actLines[i],
        diff: 'When the user enters ' + phrase + ', your program should say ' +
          JSON.stringify(expLines[i]) + ', but it said ' + JSON.stringify(actLines[i]) + '.' };
    }
  }
  if (expLines.length !== actLines.length) {
    var msg = (actLines.length < expLines.length)
      ? 'When the user enters ' + phrase + ', your program is missing some of the expected output.'
      : 'When the user enters ' + phrase + ', your program printed more than the task expects.';
    return { passed: false, actual: undefined, diff: msg };
  }
  return { passed: true, actual: undefined, diff: '' };
}

function gradeFullTranscriptCase(expectedT, studentT, normOpts, phrase) {
  var expNorm = normalizeOutput(renderFullTranscript(expectedT), normOpts);
  var actNorm = normalizeOutput(renderFullTranscript(studentT), normOpts);
  if (expNorm === actNorm) { return { passed: true, actual: undefined, diff: '' }; }
  var expL = expNorm.split('\n');
  var actL = actNorm.split('\n');
  var n = Math.min(expL.length, actL.length);
  for (var i = 0; i < n; i++) {
    if (expL[i] !== actL[i]) {
      return { passed: false, actual: actL[i],
        diff: 'When the user enters ' + phrase + ', your conversation differed on line ' + (i + 1) +
          ' — expected ' + JSON.stringify(expL[i]) + ', got ' + JSON.stringify(actL[i]) + '.' };
    }
  }
  return { passed: false, actual: undefined,
    diff: 'When the user enters ' + phrase + ', your program produced ' + actL.length +
      ' lines instead of ' + expL.length + '.' };
}

// Grade ONE case against its captured run. `{ passed, actual, diff }`.
function gradeStdinCase(kase, run, matchMode, normOpts, numericAware, floatTol) {
  var phrase = formatCaseInput(kase.stdin);
  if (!run) {
    return { passed: false, actual: undefined,
      diff: "We couldn't test your program with that input — please try running the check again." };
  }
  // Over-read: each case supplies a fixed input count matching the task, so
  // exhausting it means the student read too many inputs (NOT a hang).
  if (run.ranOutOfInput) {
    return { passed: false, actual: undefined,
      diff: 'When the user enters ' + phrase + ', your program asked for more answers than the task needs.' };
  }
  // A genuine student crash ⇒ friendly first-line message at their REAL line
  // (never freeze/leak the reference).
  if (run.exception) {
    return { passed: false, actual: (run.exception.type || null), diff: crashDiff(run) };
  }
  var expectedT = kase.expected || {};
  var studentT = run.transcript || {};
  if (matchMode === 'full-transcript') {
    return gradeFullTranscriptCase(expectedT, studentT, normOpts, phrase);
  }
  return gradePromptIndependentCase(expectedT, studentT, normOpts, numericAware, floatTol, phrase);
}

// Grade an INVALID case (tag === 'invalid') by graceful handling — the student
// must NOT crash on the bad input and must recover (read past it to the recovery
// value the case supplies), with NO comparison to any reference output. This lets
// a teacher add an invalid test right in the Input-cases table without a model
// answer needing to produce a "correct" recovered answer. `{ passed, actual, diff }`.
function gradeInvalidCase(kase, run) {
  var phrase = formatCaseInput(kase.stdin);
  if (!run) {
    return { passed: false, actual: undefined,
      diff: "We couldn't test your program with that input — please try running the check again." };
  }
  // A genuine crash on the bad input ⇒ did NOT handle it.
  if (run.exception && !run.ranOutOfInput) {
    return { passed: false, actual: (run.exception.type || null),
      diff: 'When the user enters ' + phrase + ', your program should handle it instead of crashing.' };
  }
  // Never recovered — kept asking for input past the recovery value the case gave it.
  if (run.ranOutOfInput) {
    return { passed: false, actual: undefined,
      diff: 'When the user enters ' + phrase + ', your program should recover and carry on instead of waiting for more input.' };
  }
  // Read PAST the bad input to reach the recovery value ⇒ it handled it and kept going.
  var need = (Array.isArray(kase.stdin) && kase.stdin.length) ? kase.stdin.length : 1;
  if (run.inputReads >= need) {
    return { passed: true, actual: run.inputReads, diff: '' };
  }
  return { passed: false, actual: run.inputReads,
    diff: 'When the user enters ' + phrase + ', your program should ask again for a valid value instead of moving on.' };
}

// Compare a `stdin-transcript` checkpoint. `consoleRunData` =
// `{ byStdin: { [stdinKey]: run } }` (each run carries the additive `transcript`).
// Returns `{ kind, label, passed, diff, actual }` (+ `skipped:true` for 0 cases).
export function compareStdinTranscript(checkpoint, consoleRunData) {
  consoleRunData = consoleRunData || {};
  var byStdin = consoleRunData.byStdin || {};
  var label = checkpoint.label || 'Input cases';
  var cases = Array.isArray(checkpoint.caseTable) ? checkpoint.caseTable : [];
  var matchMode = checkpoint.matchMode || 'prompt-independent';
  var normOpts = checkpoint.normalize || {};
  // numericAware defaults ON for stdin-transcript (the card's `4.5`/`4.50` goal);
  // only an explicit `false` turns it off.
  var numericAware = normOpts.numericAware !== false;

  // 0-case ⇒ skipped, never gates (mirrors compareFunctionIO).
  if (cases.length === 0) {
    return { kind: 'stdin-transcript', label: label, passed: true, skipped: true, diff: '', actual: undefined };
  }

  // Evaluate every case in boundary/invalid-first order, bucketing pass/fail by
  // tag so the student result can show one line per test category (expected /
  // boundary / invalid) — making the testing strategy visible. The overall diff
  // stays the FIRST failing case (boundary/invalid preferred), non-leaky.
  var order = orderedStdinCaseIndices(cases);
  var byTag = {};
  var firstFail = null;
  for (var k = 0; k < order.length; k++) {
    var kase = cases[order[k]] || {};
    var tag = (kase.tag === 'boundary') ? 'boundary' : (kase.tag === 'invalid') ? 'invalid' : 'expected';
    var run = byStdin[consoleRunStdinKey(kase.stdin)];
    // Invalid cases are graded on graceful handling (no crash + recovers), NOT on
    // matching a reference transcript — they carry no `expected`.
    var graded = (kase.tag === 'invalid')
      ? gradeInvalidCase(kase, run)
      : gradeStdinCase(kase, run, matchMode, normOpts, numericAware, checkpoint.floatTol);
    if (!byTag[tag]) { byTag[tag] = { tag: tag, passed: true, diff: '', actual: undefined, inputs: [] }; }
    // Record the input value(s) tested so the student result can name them (not
    // leaky — these are the inputs, never the reference output).
    byTag[tag].inputs.push(formatCaseInput(kase.stdin));
    if (!graded.passed && byTag[tag].passed) {
      byTag[tag].passed = false;
      byTag[tag].diff = graded.diff;
      byTag[tag].actual = graded.actual;
    }
    if (!graded.passed && !firstFail) { firstFail = graded; }
  }
  // Present tags in a stable, human order; only the categories actually tested.
  var tagResults = ['expected', 'boundary', 'invalid']
    .filter(function (t) { return byTag[t]; })
    .map(function (t) { return byTag[t]; });
  return {
    kind: 'stdin-transcript', label: label, passed: !firstFail,
    diff: firstFail ? firstFail.diff : '', actual: firstFail ? firstFail.actual : undefined,
    tagResults: tagResults
  };
}

// step-checks (Trello IeSYCMgQ, "Check this step"): grade ONLY the rows of a
// `stdin-transcript` checkpoint whose integer `row.step` is <= `step` (cumulative),
// with the SAME per-row verdict as `compareStdinTranscript` (`gradeStdinCase` /
// `gradeInvalidCase`, same matchMode / normalize / numericAware default / floatTol).
// The first failing row is chosen in the full Check's order (boundary, invalid,
// then expected/untagged; ties by table order). Returns:
//   { skipped: true }                          — no row tagged <= step
//   { passed: true, rows: [StepRow] }          — every graded row passed
//   { passed: false, row, rowIndex, rowStep, diff, actual, inputs, reason }
// where StepRow = { row, rowIndex, rowStep, invalid, expectedLines, summaryLine }
// (`summaryLine`: the display-only line the pass summary names, as authored) and
// `reason` (its `expectedLine` / `actualLine` as authored / as printed)
// is the DISPLAY-ONLY structured cause the step-check copy is built from (see
// stepFailReason). `diff` / `actual` are the grader's own. Changes nothing the full
// Check calls.
function isStepTag(v) {
  return typeof v === 'number' && isFinite(v) && Math.floor(v) === v && v >= 1;
}

// Why a step-check row failed, worked out ONLY after the grader said it failed.
// kinds: 'line' (first differing result line), 'empty' (printed nothing), 'short'
// (stopped before a line), 'extra' (printed a line too many), 'crash', 'no-recovery'
// (an invalid row that moved on instead of asking again), 'other' (no copy line:
// no run, asked for too many inputs, a full-transcript difference — the caller
// falls back to the grader's `diff`).
function stepFailReason(kase, run, invalid, matchMode, normOpts, numericAware, floatTol) {
  if (!run) { return { kind: 'other' }; }
  if (invalid) {
    if (run.exception && !run.ranOutOfInput) { return { kind: 'crash' }; }
    if (run.ranOutOfInput) { return { kind: 'other' }; }
    return { kind: 'no-recovery' };
  }
  if (run.ranOutOfInput) { return { kind: 'other' }; }
  if (run.exception) { return { kind: 'crash' }; }
  if (matchMode === 'full-transcript') { return { kind: 'other' }; }
  var expLines = transcriptResultLines(kase.expected || {}, normOpts);
  var actLines = transcriptResultLines(run.transcript || {}, normOpts);
  // Compared in the normalised form; SHOWN as authored / as printed.
  var expShow = transcriptDisplayLines(kase.expected || {}, normOpts);
  var actShow = transcriptDisplayLines(run.transcript || {}, normOpts);
  var n = Math.min(expLines.length, actLines.length);
  for (var i = 0; i < n; i++) {
    if (!stdinLineMatches(expLines[i], actLines[i], numericAware, floatTol)) {
      return { kind: 'line', expectedLine: shownLine(expShow, expLines, i), actualLine: shownLine(actShow, actLines, i) };
    }
  }
  if (actLines.length < expLines.length) {
    return { kind: actLines.length === 0 ? 'empty' : 'short', expectedLine: shownLine(expShow, expLines, n) };
  }
  if (actLines.length > expLines.length) {
    return { kind: 'extra', actualLine: shownLine(actShow, actLines, n) };
  }
  return { kind: 'other' };
}

export function gradeStepCheck(checkpoint, consoleRunData, step) {
  checkpoint = checkpoint || {};
  consoleRunData = consoleRunData || {};
  var byStdin = consoleRunData.byStdin || {};
  var cases = Array.isArray(checkpoint.caseTable) ? checkpoint.caseTable : [];
  var matchMode = checkpoint.matchMode || 'prompt-independent';
  var normOpts = checkpoint.normalize || {};
  var numericAware = normOpts.numericAware !== false;
  var order = orderedStdinCaseIndices(cases).filter(function (i) {
    var c = cases[i];
    return c && isStepTag(c.step) && c.step <= step;
  });
  if (order.length === 0) { return { skipped: true }; }
  // The pass summary names a row by its first expected result line that is NOT
  // shared by every (non-invalid) row of this checkpoint at the same position (1.9's
  // rows all open with the same greeting), or by its last line when all are shared.
  var allLines = [];
  for (var c = 0; c < cases.length; c++) {
    if (cases[c] && cases[c].tag !== 'invalid') {
      allLines.push(transcriptResultLines(cases[c].expected || {}, normOpts));
    }
  }
  var commonLead = commonLeadingLines(allLines);
  var rows = [];
  for (var k = 0; k < order.length; k++) {
    var idx = order[k];
    var kase = cases[idx];
    var invalid = kase.tag === 'invalid';
    var run = byStdin[consoleRunStdinKey(kase.stdin)];
    var graded = invalid
      ? gradeInvalidCase(kase, run)
      : gradeStdinCase(kase, run, matchMode, normOpts, numericAware, checkpoint.floatTol);
    if (!graded.passed) {
      return {
        passed: false, row: kase, rowIndex: idx, rowStep: kase.step,
        diff: graded.diff, actual: graded.actual,
        inputs: Array.isArray(kase.stdin) ? kase.stdin.slice() : [],
        reason: stepFailReason(kase, run, invalid, matchMode, normOpts, numericAware, checkpoint.floatTol)
      };
    }
    var expected = invalid ? [] : transcriptResultLines(kase.expected || {}, normOpts);
    var at = Math.min(commonLead, expected.length - 1);
    rows.push({ row: kase, rowIndex: idx, rowStep: kase.step, invalid: invalid,
      expectedLines: expected,
      summaryLine: expected.length === 0 ? '' :
        shownLine(transcriptDisplayLines(kase.expected || {}, normOpts), expected, at) });
  }
  // Report the passing rows in table order (the copy builder groups them).
  rows.sort(function (a, b) { return a.rowIndex - b.rowIndex; });
  return { passed: true, rows: rows };
}

// step-checks-prompts-functions (Trello EiSNIhrA): the prompt and function step checks.
//
// A prompt match normalises BOTH sides the same way and nothing else (decisions A2):
// lower-case, collapse runs of SPACES (U+0020 only) to one, trim leading/trailing spaces.
export function normalizeStepPrompt(text) {
  return String(text == null ? '' : text).toLowerCase().replace(/ +/g, ' ').replace(/^ +| +$/g, '');
}

// The "prompt lines" of a run (decisions D1/A1): the cursor-line text after the LAST
// newline of each turn's `outputBefore`, plus — only when the run ran out of input —
// the cursor-line text of `tail`. That is exactly what an `input("...")` prompt looks
// like on screen, and it is EMPTY for `print("Type: ")` followed by `input("")`.
// Returned as printed (not normalised), in run order.
export function stepPromptLines(run) {
  var t = (run && run.transcript) || {};
  var turns = Array.isArray(t.turns) ? t.turns : [];
  var segments = [];
  for (var i = 0; i < turns.length; i++) { segments.push(turns[i] && turns[i].outputBefore); }
  if (run && run.ranOutOfInput) { segments.push(t.tail); }
  return segments.map(function (seg) {
    var s = String(seg == null ? '' : seg).replace(/\r\n?/g, '\n');
    return s.slice(s.lastIndexOf('\n') + 1);
  });
}

// `gradePromptStepCheck(checkpoint, consoleRunData, step)` → `{ skipped:true }` |
// `{ passed:true, family:'prompt', prompts:[{ step, text }] }` |
// `{ passed:false, family:'prompt', rowStep, prompt, asked? }`.
// Every `stepPrompts` entry tagged `step <= N` is graded against ITS OWN probe run,
// `byStdin[consoleRunStdinKey(entry.stdin)]` (A1); it passes when ANY prompt line of
// that run normalises equal (a prompt printed twice or in a loop passes). The failure
// reported is the earliest-step failing entry (ties: list order). `asked` is the last
// non-empty prompt line of that probe, as printed — absent when there is none (A3).
// An entry whose text normalises to '' is ignored, never passed (it would otherwise
// match print-then-input("")'s empty cursor line).
export function gradePromptStepCheck(checkpoint, consoleRunData, step) {
  checkpoint = checkpoint || {};
  var byStdin = (consoleRunData && consoleRunData.byStdin) || {};
  var entries = (Array.isArray(checkpoint.stepPrompts) ? checkpoint.stepPrompts : [])
    .map(function (e, i) { return { e: e, i: i }; })
    .filter(function (x) {
      return x.e && isStepTag(x.e.step) && x.e.step <= step && normalizeStepPrompt(x.e.text) !== '';
    });
  if (entries.length === 0) { return { skipped: true }; }
  entries.sort(function (a, b) { return (a.e.step - b.e.step) || (a.i - b.i); });
  var passed = [];
  for (var k = 0; k < entries.length; k++) {
    var entry = entries[k].e;
    var want = normalizeStepPrompt(entry.text);
    var run = byStdin[consoleRunStdinKey(entry.stdin)];
    var lines = run ? stepPromptLines(run) : [];
    var hit = lines.some(function (l) { return normalizeStepPrompt(l) === want; });
    if (!hit) {
      var fail = { passed: false, family: 'prompt', rowStep: entry.step, prompt: String(entry.text) };
      for (var j = lines.length - 1; j >= 0; j--) {
        if (normalizeStepPrompt(lines[j]) !== '') { fail.asked = lines[j]; break; }
      }
      return fail;
    }
    passed.push({ step: entry.step, text: String(entry.text) });
  }
  return { passed: true, family: 'prompt', prompts: passed };
}

// The one pass line for a function case (pre-rendered here so challenge-step-check.js
// stays import-free; the same formatCall/formatValue as the full Check's messages).
function functionCasePassLine(fname, kase, compareMode) {
  var call = formatCall(fname, kase.args, caseStdin(kase));
  var gives = formatValue(kase.expected);
  var prints = quotePrintedLines(kase.expectedStdout);
  if (compareMode === 'prints') { return call + ' prints ' + prints + '.'; }
  if (compareMode === 'both') { return call + ' gives ' + gives + ' and prints ' + prints + '.'; }
  return call + ' gives ' + gives + '.';
}

// `gradeFunctionStepCheck(checkpoint, fncallData, step)` → `{ skipped:true }` |
// `{ passed:true, family:'function', cases:[{ step, line }] }` |
// `{ passed:false, family:'function', rowStep, functionName, diff }`.
// Grades a COPY of the function-io checkpoint holding only the cases tagged
// `step <= N` (A8: cases above N are never reported "could not be run") with the
// full Check's own comparator, `compareFunctionIO` — results are matched by name +
// args + stdin there, so `fncallData` is passed through whole. On a failure the reported step
// is the EARLIEST failing case's step, and `diff` is re-derived from that step's
// cases only, so the sentence belongs to the step it names. A missing function has
// no per-case evidence: it reports the subset's earliest step with the full Check's
// "couldn't find a function" wording (A7).
export function gradeFunctionStepCheck(checkpoint, fncallData, step) {
  checkpoint = checkpoint || {};
  var all = Array.isArray(checkpoint.cases) ? checkpoint.cases : [];
  var subset = all.filter(function (c) { return c && isStepTag(c.step) && c.step <= step; });
  if (subset.length === 0) { return { skipped: true }; }
  var mode = checkpoint.compareMode || 'return';
  var r = compareFunctionIO(Object.assign({}, checkpoint, { cases: subset }), fncallData);
  if (r.passed) {
    return { passed: true, family: 'function', functionName: checkpoint.functionName,
      cases: subset.map(function (c) {
        return { step: c.step, line: functionCasePassLine(checkpoint.functionName, c, mode) };
      }) };
  }
  var minStep = Infinity;
  var evidence = Array.isArray(r.evidence) ? r.evidence : null;
  for (var i = 0; i < subset.length; i++) {
    if ((!evidence || (evidence[i] && evidence[i].passed === false)) && subset[i].step < minStep) {
      minStep = subset[i].step;
    }
  }
  var diff = r.diff;
  if (evidence) {
    var atStep = subset.filter(function (c) { return c.step === minStep; });
    var r2 = compareFunctionIO(Object.assign({}, checkpoint, { cases: atStep }), fncallData);
    if (!r2.passed && r2.diff) { diff = r2.diff; }
  }
  return { passed: false, family: 'function', rowStep: minStep,
    functionName: checkpoint.functionName, diff: String(diff || '') };
}

// Compare one checkpoint against the actual (student) values object (already
// filtered to JSON-serializable scalars/plain lists/dicts by the runner).
// `runStdout` (additive 3rd arg) carries the student's captured console output,
// only consulted for `kind: 'expected-output'` checkpoints.
// `passData` (additive 4th arg — code-challenge-exception-checkpoints refactored
// the former positional `valueSeries`/`traceData`/`fncallData` args into ONE
// context object so the growing per-pass blob list stops widening the signature)
// bundles the optional per-pass blobs:
//   { valueSeries, discreteSeries, traceData, fncallData, consoleRunData }
//   - valueSeries  (code-challenge-temporal-checkpoints): per-variable frame
//     series `{ [variable]: (number|null)[] }`, consumed ONLY by temporal kinds.
//   - discreteSeries (code-challenge-flag-state-checkpoints): per-variable frame
//     series `{ [variable]: (boolean|string|null)[] }`, consumed ONLY by the
//     discrete-state kinds (flag-becomes / state-sequence).
//   - traceData    (code-challenge-behavioral-structural): `{ behavioral, structural }`,
//     consumed ONLY by behavioral/structural kinds.
//   - fncallData   (code-challenge-function-io): `{ results, functions }`,
//     consumed ONLY by `function-io` kinds.
//   - consoleRunData (code-challenge-exception-checkpoints): `{ byStdin: { [key]: run } }`,
//     consumed ONLY by console-run kinds (runs-clean/raises-on/handles-gracefully).
// Defaults to `{}` and every blob is read defensively (each may be undefined), so
// a caller that only needs one family passes just that blob.
//
// advisory-checkpoints (D1): this is the INTERNAL body. The exported
// `compareCheckpoint` below is a thin wrapper that stamps the advisory
// (`hintOnly`) marker onto the result. Do NOT call this directly from
// production code — call the export, or the marker is lost.
function gradeCheckpoint(checkpoint, actualValues, runStdout, passData) {
  passData = passData || {};

  if (checkpoint && checkpoint.kind === 'expected-output') {
    return compareExpectedOutput(checkpoint, runStdout);
  }

  if (checkpoint && checkpoint.kind === 'function-io') {
    return compareFunctionIO(checkpoint, passData.fncallData);
  }

  if (checkpoint && isConsoleRunKind(checkpoint.kind)) {
    return compareConsoleRun(checkpoint, passData.consoleRunData);
  }

  // code-challenge-stdin-transcript (grading v3 [3/3]): multi-case stdin. Routes
  // to compareStdinTranscript (NOT compareConsoleRun) — it reads the SAME
  // consoleRunData.byStdin structure but grades the interleaved transcript.
  if (checkpoint && checkpoint.kind === 'stdin-transcript') {
    return compareStdinTranscript(checkpoint, passData.consoleRunData);
  }

  if (checkpoint && checkpoint.kind === 'behavioral') {
    return compareBehavioral(checkpoint, passData.traceData && passData.traceData.behavioral);
  }

  if (checkpoint && checkpoint.kind === 'structural') {
    return compareStructural(checkpoint, passData.traceData && passData.traceData.structural);
  }

  // code-challenge-flag-state-checkpoints (G1/R3): discrete-state kinds are in
  // TEMPORAL_KINDS, so they MUST be routed here — ABOVE the generic temporal
  // branch — to read the DISCRETE series (bool/string), not the numeric one.
  // Read defensively: a Save-path caller legitimately passes no discreteSeries,
  // and compareDiscrete's empty-series guard then friendly-fails (never throws).
  if (checkpoint && isDiscreteKind(checkpoint.kind)) {
    var discrete = (passData.discreteSeries && passData.discreteSeries[checkpoint.variable]) || null;
    return compareDiscrete(checkpoint, discrete);
  }

  // code-challenge-collision-oscillation-liveness: route the three new numeric
  // kinds ABOVE the generic temporal branch. `relational-series` needs the WHOLE
  // per-variable series map (its actors live in `expr`, no single `variable`); the
  // other two read their own `variable`'s series defensively.
  if (checkpoint && checkpoint.kind === 'relational-series') {
    return evalRelationalSeries(checkpoint, passData.valueSeries || {});
  }
  if (checkpoint && checkpoint.kind === 'oscillation') {
    var oscSeries = (passData.valueSeries && passData.valueSeries[checkpoint.variable]) || null;
    return evalOscillation(checkpoint, oscSeries);
  }
  if (checkpoint && checkpoint.kind === 'changed-from-initial') {
    var livSeries = (passData.valueSeries && passData.valueSeries[checkpoint.variable]) || null;
    return evalChangedFromInitial(checkpoint, livSeries);
  }

  // code-challenge-causal-event-checkpoints: route the two new kinds ABOVE the
  // generic temporal branch — both key on `observe`, not `variable`, and
  // `causal-delta` additionally needs to check `discreteSeries` first.
  if (checkpoint && checkpoint.kind === 'causal-delta') {
    return evalCausalDelta(checkpoint, passData);
  }
  if (checkpoint && checkpoint.kind === 'event-normalized') {
    return evalEventNormalized(checkpoint, passData);
  }

  // code-challenge-dotted-path-checkpoints: route path-temporal ABOVE the generic
  // temporal branch — it reads its series by `path` (not `variable`) and may carry
  // the orchestrator's skip marker.
  if (checkpoint && checkpoint.kind === 'path-temporal') {
    return comparePathTemporal(checkpoint, passData);
  }

  if (checkpoint && isTemporalKind(checkpoint.kind)) {
    var series = (passData.valueSeries && passData.valueSeries[checkpoint.variable]) || null;
    return compareTemporal(checkpoint, series);
  }

  var values = actualValues || {};
  var variable = checkpoint.variable;
  var expected = checkpoint.expectedValue;
  var label = checkpoint.label || variable;

  if (!Object.prototype.hasOwnProperty.call(values, variable)) {
    return { variable: variable, label: label, expected: expected, actual: undefined, passed: false, notFound: true };
  }

  var actual = values[variable];
  var passed;
  if (typeof expected === 'number' && typeof actual === 'number'
      && typeof checkpoint.tolerance === 'number' && checkpoint.tolerance > 0) {
    passed = Math.abs(actual - expected) <= checkpoint.tolerance;
  } else {
    passed = deepEqual(expected, actual);
  }

  return { variable: variable, label: label, expected: expected, actual: actual, passed: passed, notFound: false };
}

// advisory-checkpoints (D1): stamp the advisory marker ONCE, here, rather than in
// each of the twelve result builders that construct result objects. This function
// is the SINGLE export and the single production entry point (the internal
// `compareAll` caller, both `challenge-record-chrome.js` call sites and every spec
// route through it), so nothing can bypass the stamp and no future checkpoint kind
// can forget it — the documented failure mode of the per-builder approach.
//
// It only ever ADDS the flag:
//   - never writes `false` (an unflagged checkpoint must still yield
//     `r.hintOnly === undefined`; a standing `steps-down` spec asserts that), and
//   - never overwrites a flag an evaluator already set, so `compareStructural`'s
//     hardcoded `true` and `evalChangedFromInitial`'s advisory-by-default
//     (`hintOnly !== false`) convention are both preserved exactly.
// The test is strictly `=== true`, matching `overallPassed`'s own test: the flag is
// seed-authored and typed `Boolean` in the Mongoose schema, so only `true` can
// legitimately reach here. Temporal kinds therefore still GATE by default.
export function compareCheckpoint(checkpoint, actualValues, runStdout, passData) {
  var r = gradeCheckpoint(checkpoint, actualValues, runStdout, passData);
  if (checkpoint && checkpoint.hintOnly === true && r && r.hintOnly !== true) {
    r.hintOnly = true;
  }
  return r;
}

export function compareAll(checkpoints, actualValues, runStdout, passData) {
  passData = passData || {};
  return (checkpoints || []).map(function (cp) {
    return compareCheckpoint(cp, actualValues, runStdout, passData);
  });
}

// A Check passes only when there's at least one GATING checkpoint AND every
// gating checkpoint passed. Hint-only `structural` rows are EXCLUDED from the
// gate (code-challenge-behavioral-structural D7): a structural miss can never
// fail a Check on its own. code-challenge-function-io: a SKIPPED function-io
// result (0 cases) is also excluded — it is neither a vacuous pass nor a
// spurious fail. An empty gating list can never be published (see the
// server-side gating gate), so requiring ≥1 gating row is mostly defensive.
export function overallPassed(results) {
  var gating = (results || []).filter(function (r) {
    // code-challenge-collision-oscillation-liveness: hint-only rows (the liveness
    // baseline, and structural rows which already carry hintOnly) NEVER gate — a
    // failing hint-only row must not sink the student's overall verdict.
    return !(r && (r.kind === 'structural' || r.skipped || r.hintOnly === true));
  });
  return gating.length > 0 && gating.every(function (r) { return r.passed; });
}

// ---------------------------------------------------------------------------
// code-challenge-game-fairness (game-mutation-and-paired-check): a small,
// exported, Node-unit-testable kind taxonomy + save-time mutant builders shared
// by the record chrome's game-mode fairness advisory and the paired-check
// authoring nudge. Kinds are classified by whether a frozen/hardcoded CONSTANT
// can SURVIVE the kind, so the two advisories always agree in direction (design
// D3). No DOM, no network — pure.

// STRONG_KINDS — a constant provably FAILS these, so they resist gaming. Only
// `crosses-threshold` ships today (a constant never straddles the target); cards
// #6/#7 add causal-delta / event-normalized / relational / oscillation here with
// NO rule change (the set just grows).
// code-challenge-flag-state-checkpoints (D7): `state-sequence` is strong (an
// ordered progression through states is gaming-resistant — a constant can only
// ever sit on one state).
// code-challenge-collision-oscillation-liveness (D1/D6): `relational-series` and
// `oscillation` are STRONG — a frozen/hardcoded constant provably fails them (a
// constant never satisfies a distance-under-r predicate over a moving target, and
// a flat series has zero reversals). So the paired-check nudge treats a collision
// or oscillation gating check as sufficient interaction coverage.
// code-challenge-causal-event-checkpoints (D2/D5/D6): `causal-delta` and
// `event-normalized` join STRONG_KINDS by membership alone — a hardcoded
// constant provably fails both (it never responds to input; it never changes
// at a per-event rate) — no additional advisory logic is needed.
// code-challenge-dotted-path-checkpoints: `path-temporal` is UNIFORMLY strong
// (not per-instance / not inherited from the wrapped pathCheck.type) — it grades
// state inside an object, exactly the surface hardcoding/freezing/jump-to-final
// gaming patterns target, and STRONG_KINDS has no per-instance mechanism.
// game-checkpoint-false-negative: `steps-down` is STRONG by membership alone —
// a frozen or hardcoded CONSTANT series contains no decreasing pair, so both
// mutant builders provably fail it, and so does an only-ever-rising counter.
// It is NOT in WEAK_KINDS.
var STRONG_KINDS = {
  'crosses-threshold': 1, 'state-sequence': 1, 'relational-series': 1, oscillation: 1,
  'causal-delta': 1, 'event-normalized': 1, 'path-temporal': 1, 'steps-down': 1
};
// WEAK_KINDS — a constant / hardcoded value can PASS these (gameable alone).
// `monotonic` is weak (a flat series is non-decreasing). `exact`/`tolerance` are
// the legacy final-value checkpoints (which carry no temporal `kind`) — see
// checkpointKindLabel. `flag-becomes` is weak (a top-of-file `game_over = True`
// passes it) — code-challenge-flag-state-checkpoints D7.
// code-challenge-collision-oscillation-liveness (D7): `changed-from-initial`
// (liveness) is WEAK — a single one-frame change trivially satisfies it — so if
// the optional "require this" toggle makes it gate, the paired-check nudge still
// correctly flags an all-weak set.
var WEAK_KINDS = {
  reached: 1, settled: 1, 'stays-within': 1, monotonic: 1, exact: 1, tolerance: 1,
  'flag-becomes': 1, 'changed-from-initial': 1
};

export function isStrongKind(kind) {
  return !!(kind && Object.prototype.hasOwnProperty.call(STRONG_KINDS, kind));
}
export function isWeakKind(kind) {
  return !!(kind && Object.prototype.hasOwnProperty.call(WEAK_KINDS, kind));
}

// Map a saved checkpoint OBJECT to its taxonomy label. Temporal checkpoints carry
// an explicit `kind`; a legacy final-value checkpoint (buildTemporalRow's
// non-numeric / exact row) carries no `kind` — classify it as 'tolerance' when it
// has a positive numeric tolerance, else 'exact'. Non-game kinds pass through
// unchanged (they are neither weak nor strong).
export function checkpointKindLabel(cp) {
  if (!cp) { return null; }
  if (cp.kind) { return cp.kind; }
  if (Object.prototype.hasOwnProperty.call(cp, 'expectedValue')) {
    return (typeof cp.tolerance === 'number' && cp.tolerance > 0) ? 'tolerance' : 'exact';
  }
  return null;
}

var PAIRED_CHECK_MESSAGE =
  'Your checks could be satisfied by a program that just sits on the answer ' +
  'without really playing. Add a check that requires the value to CHANGE during ' +
  'play (e.g. a crosses-threshold check).';

// pairedCheckAdvice — the authoring nudge: fires when the ticked gating set has
// ≥1 weak (constant-passable) kind and NO strong (constant-failing) kind. Pure;
// returns the nudge string or null.
export function pairedCheckAdvice(gatingCheckpoints) {
  var labels = (gatingCheckpoints || []).map(checkpointKindLabel);
  var hasWeak = labels.some(isWeakKind);
  var hasStrong = labels.some(isStrongKind);
  return (hasWeak && !hasStrong) ? PAIRED_CHECK_MESSAGE : null;
}

// plain-pygame-authoring-policy (B): the plain-pygame weak-check reminder. Plain
// pygame grades final values only, and every plain-pygame checkpoint is weak
// (exact/tolerance) — a program that hardcodes the final value would pass. Steer
// to Pygame Zero (NOT crosses-threshold, which plain pygame cannot produce).
var PLAIN_PYGAME_PAIRED_CHECK_MESSAGE =
  'A program that just hardcodes this final value would pass these checks. If you ' +
  'want to make sure the value really changes during play, author the activity as ' +
  'a Pygame Zero project.';

// plainPygamePairedCheckAdvice — fires when the saved plain-pygame challenge has
// ≥1 weak gating (ticked) final-value checkpoint, else null. Pure; mirrors
// pairedCheckAdvice but with no strong-kind escape (plain pygame has none).
export function plainPygamePairedCheckAdvice(gatingCheckpoints) {
  var hasWeak = (gatingCheckpoints || []).map(checkpointKindLabel).some(isWeakKind);
  return hasWeak ? PLAIN_PYGAME_PAIRED_CHECK_MESSAGE : null;
}

// --- Mutant blob builders (browser save-time advisory) -----------------------
// Each active operator fabricates a blob with BOTH a per-variable `valueSeries`
// (same length as the demo, every sample a clean numeric constant, no null holes)
// AND a matching `finalValues` — so the mutant re-grades temporal rows AND the
// exact/tolerance final-value rows (the hardcoded-final-value case is the most
// gameable and must trip the advisory). Input is a normalized "demo row" array:
//   { variable, series:(number|null)[], target?:number, final?:*, kind?, lo?, hi? }
// No-non-null fallback (REVIEWER-1 G1): a variable whose demo series has NO
// non-null sample is non-gradable / ungameable — reuse its series UNCHANGED and
// its demo final; never invent a constant.
function firstNonNull(series) {
  for (var i = 0; i < series.length; i++) { if (isNum(series[i])) { return series[i]; } }
  return null;
}
function constantSeries(series, k) {
  return series.map(function () { return k; });
}
function buildMutant(demoRows, constFor) {
  var out = { series: {}, finalValues: {} };
  (demoRows || []).forEach(function (r) {
    var series = Array.isArray(r.series) ? r.series : [];
    if (firstNonNull(series) === null) {
      // Ungameable variable: reuse the demo series + demo final unchanged.
      out.series[r.variable] = series.slice();
      out.finalValues[r.variable] = r.final;
      return;
    }
    var k = constFor(r, series);
    out.series[r.variable] = constantSeries(series, k);
    out.finalValues[r.variable] = k;
  });
  return out;
}

// freeze — nothing moves: constant = the FIRST non-null demo sample.
export function buildFreezeMutant(demoRows) {
  return buildMutant(demoRows, function (r, series) { return firstNonNull(series); });
}

// hardcode-the-target — jump straight to the answer: constant = the checkpoint's
// numeric `target` when present; else the range midpoint for stays-within; else
// the demo final; else the first non-null sample.
export function buildHardcodeMutant(demoRows) {
  return buildMutant(demoRows, function (r, series) {
    if (typeof r.target === 'number' && !isNaN(r.target)) { return r.target; }
    if (r.kind === 'stays-within' && typeof r.lo === 'number' && typeof r.hi === 'number') {
      return (r.lo + r.hi) / 2;
    }
    if (typeof r.final === 'number' && !isNaN(r.final)) { return r.final; }
    return firstNonNull(series);
  });
}

export { deepEqual, STRONG_KINDS, WEAK_KINDS };
